import { Component, HostListener, NgZone, OnInit } from '@angular/core';

import { environment } from '@env/environment';

import { AuthQuery } from '@app/auth/auth.query';
import {
  EventConfigService,
  EventDay,
  ContentBlock,
  ContentImage,
  ProgramTab,
  AgendaItem,
  SaveContentBlock,
  EmailTemplate,
  SaveEmailTemplate,
  ConfigFileMeta,
  SaveI18nEntry,
  GalleryImage,
  GalleryMeta,
  GalleryCategoryMeta,
  AdminUser,
  SysInfoResponse,
  MigrationResult,
} from '@app/services/event-config.service';
import { ExhibitorService } from '@app/services/exhibitor.service';
import { CONTENT_DEFAULTS } from './content-defaults';
import {
  LEGAL_SECTIONS,
  LegalSectionDef,
  LegalRole,
  LegalType,
  legalDefault,
} from './legal-defaults';
import { parseImport } from './config-import-parsers';
import { ACTIVITY_DEFAULTS, CATEGORY_DEFAULTS } from './list-defaults';
import { EMAIL_DEFAULTS } from './email-defaults';
import { I18N_DEFAULTS } from './i18n-defaults';
import { EVENT_PHASES, DEFAULT_PHASE_ID, phaseRuntimeFlags } from './event-phases';

interface I18nRow {
  key: string;
  values: { [lang: string]: string };
}

type ContentRole = 'visitor' | 'exhibitor' | 'system';

interface ContentSection {
  key: string;
  label: string;
  kind: 'prose' | 'program';
  role: ContentRole;
}

// Style overrides injected into the TinyMCE iframe alongside the Tailwind Play
// CDN, mirroring the custom <style> block in eplus/_inc/meta.inc so the editor
// preview matches the published page.
const EDITOR_CONTENT_OVERRIDES = `
body { padding: 12px; }
.bg-gray-800 { --tw-bg-opacity: 1; background-color: #023163; }
.maintitle { font-size: 42px; padding-top: 20px; }
h2 { font-size: 2.25rem !important; line-height: 2.5rem !important; }
.text-blue-600 { color: #013366 !important; font-weight: 800; }
.border-blue-600 { border-color: #013366 !important; }
`;

interface EventForm {
  title: string;
  year: string;
  www_url: string;
  email: string;
  frontend_url: string;
  // Welcome dialog heading (public welcome page <h1>) and body, both stored in
  // the 'event' group so they save via the API and the public page reads them
  // from the API at boot.
  welcome_heading: string;
  welcome: string;
  // operational text (stored in the 'event' group, edited in the Operations tab)
  mail_subject: string;
  complete_profile_by: string;
  validate_registration_latest: string;
}

interface FlagsForm {
  booking_stop: boolean;
  show_gallery: boolean;
  exhibitor_booking_active: boolean;
  test_version: boolean;
}

interface NotificationsForm {
  cancel_appointment: boolean;
  create_appointment: boolean;
  password_lost: boolean;
  register_new: boolean;
  approve_visitor: boolean;
  activate_visitor: boolean;
  approve_exhibitor: boolean;
  activate_exhibitor: boolean;
  sendaccountinfo: boolean;
  sendreminder: boolean;
}

interface TestmailForm {
  visitor: string;
  exhibitor: string;
  atout: string;
}

interface LogisticsForm {
  hotel: string;
  event_space: string;
  event_date: string;
  book_night: string;
  cancel_date: string;
  day1: string;
  day2: string;
}

interface MediaForm {
  brand_logo: string;
  nav_background: string;
  hero_video_mp4: string;
  hero_video_webm: string;
  email_logo: string;
  pdf_logo: string;
  pdf_footer: string;
}

@Component({
  templateUrl: './event-config.component.html',
  styleUrls: ['./event-config.component.scss'],
})
export class EventConfigComponent implements OnInit {
  loading = true;
  message = '';

  // ---- Reusable confirmation dialog -----------------------------------
  // A styled in-app replacement for the native confirm() for destructive
  // actions (e.g. deleting a gallery image). Open it via openConfirm(); the
  // template renders the modal and calls confirmDialogConfirm()/Cancel().
  confirmDialog: {
    open: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel: string;
    tone: 'danger' | 'primary';
    imageUrl: string;
    onConfirm: (() => void) | null;
  } = {
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    tone: 'danger',
    imageUrl: '',
    onConfirm: null,
  };

  private openConfirm(opts: {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    tone?: 'danger' | 'primary';
    imageUrl?: string;
    onConfirm: () => void;
  }): void {
    this.confirmDialog = {
      open: true,
      title: opts.title,
      message: opts.message,
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      tone: opts.tone || 'danger',
      imageUrl: opts.imageUrl || '',
      onConfirm: opts.onConfirm,
    };
  }

  confirmDialogCancel(): void {
    this.confirmDialog.open = false;
    this.confirmDialog.onConfirm = null;
  }

  confirmDialogConfirm(): void {
    const fn = this.confirmDialog.onConfirm;
    this.confirmDialog.open = false;
    this.confirmDialog.onConfirm = null;
    if (fn) {
      fn();
    }
  }

  // ---- Gallery lightbox -----------------------------------------------
  // Full-screen viewer for the gallery images with prev/next navigation.
  lightbox: { open: boolean; images: GalleryImage[]; index: number } = {
    open: false,
    images: [],
    index: 0,
  };

  openLightbox(images: GalleryImage[], index: number): void {
    if (!images || images.length === 0) {
      return;
    }
    this.lightbox = { open: true, images, index: Math.max(0, Math.min(index, images.length - 1)) };
  }

  lightboxClose(): void {
    this.lightbox.open = false;
    this.lightbox.images = [];
    this.lightbox.index = 0;
  }

  lightboxPrev(): void {
    if (!this.lightbox.open || this.lightbox.images.length === 0) {
      return;
    }
    const n = this.lightbox.images.length;
    this.lightbox.index = (this.lightbox.index - 1 + n) % n;
  }

  lightboxNext(): void {
    if (!this.lightbox.open || this.lightbox.images.length === 0) {
      return;
    }
    const n = this.lightbox.images.length;
    this.lightbox.index = (this.lightbox.index + 1) % n;
  }

  lightboxCurrent(): GalleryImage | null {
    if (!this.lightbox.open || this.lightbox.images.length === 0) {
      return null;
    }
    return this.lightbox.images[this.lightbox.index] || null;
  }

  @HostListener('document:keydown', ['$event'])
  onLightboxKeydown(event: KeyboardEvent): void {
    if (!this.lightbox.open) {
      return;
    }
    if (event.key === 'Escape') {
      this.lightboxClose();
    } else if (event.key === 'ArrowLeft') {
      this.lightboxPrev();
    } else if (event.key === 'ArrowRight') {
      this.lightboxNext();
    }
  }

  // Inline feedback for the Content tab Save button (shown next to it, since the
  // global `message` banner sits at the top of the page and is easy to miss).
  contentSaving = false;
  contentStatus = '';
  contentStatusError = false;

  client = '';

  // ---- Event lifecycle phase (arrow bar above the tabs) ----------------
  // Tab-like navigator: selecting an arrow only switches the phase panel
  // (no side effects). The panels carry quick controls (booking toggles,
  // gallery) plus a button to jump to the full-management tab. The chosen
  // phase is persisted (ckey 'phase') and surfaced in the dashboard header.
  readonly phases = EVENT_PHASES;
  activePhase: string = DEFAULT_PHASE_ID;

  // Bound to the main mat-tab-group so the phase panels can jump to a tab.
  mainTabIndex = 0;

  // Top-level tab order, used by goToTab() to resolve a label to its index.
  private readonly tabOrder = [
    'Event',
    'Content',
    'Legal',
    'Agenda',
    'Logistics',
    'Media',
    'Gallery',
    'Operations',
    'E-mail',
    'System',
    'Languages',
    'Lists',
    'Config files',
    'Admins',
    'Sysadmin',
  ];

  // ---- Sysadmin tab: system info + manual DB migration -----------------
  readonly env = environment;
  sysInfo: SysInfoResponse | null = null;
  sysInfoLoading = false;
  sysInfoError = '';
  migrating = false;
  migrationResult: MigrationResult | null = null;
  migrationError = '';

  // ---- Import / bootstrap from an old config file ----------------------
  // Import lives in a toggled panel above the tabs (not a tab itself).
  importOpen = false;
  importText = '';
  importFileName = '';
  importMessage = '';
  // Config files staged by an import; written by saveAll().
  pendingDynamicFields: any = null;
  pendingBookingRestrictions: any = null;
  // Bound (not a static attribute) so the literal JSON braces don't trip the
  // Angular template ICU/interpolation parser.
  readonly importPlaceholder =
    '{ "event": { "title": "Bonjour France 2026", "year": "2026" }, "account_info": { "hotel": "Crowne Plaza Marseille" }, "event_cities": [ ... ] }';

  // available system languages to pick from
  readonly availableLanguages = [
    { code: 'de', label: 'Deutsch' },
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' },
  ];

  event: EventForm = {
    title: '',
    year: '',
    www_url: '',
    email: '',
    frontend_url: '',
    welcome_heading: '',
    welcome: '',
    mail_subject: '',
    complete_profile_by: '',
    validate_registration_latest: '',
  };

  // ---- Operations (Stage 5A): flags / notifications / test recipients ----
  // Defaults mirror the legacy conf so an unsaved event behaves as before.
  flags: FlagsForm = {
    booking_stop: false,
    show_gallery: false,
    exhibitor_booking_active: true,
    test_version: false,
  };

  notifications: NotificationsForm = {
    cancel_appointment: true,
    create_appointment: true,
    password_lost: true,
    register_new: true,
    approve_visitor: false,
    activate_visitor: false,
    approve_exhibitor: false,
    activate_exhibitor: false,
    sendaccountinfo: true,
    sendreminder: true,
  };

  testmail: TestmailForm = {
    visitor: '',
    exhibitor: '',
    atout: '',
  };

  // Labels for the notification toggles, rendered in the Operations tab.
  readonly notificationLabels: { key: keyof NotificationsForm; label: string }[] = [
    { key: 'cancel_appointment', label: 'Appointment cancelled' },
    { key: 'create_appointment', label: 'Appointment created' },
    { key: 'password_lost', label: 'Password reset requested' },
    { key: 'register_new', label: 'New registration (admin)' },
    { key: 'approve_visitor', label: 'Visitor approved' },
    { key: 'activate_visitor', label: 'Visitor activated' },
    { key: 'approve_exhibitor', label: 'Exhibitor approved' },
    { key: 'activate_exhibitor', label: 'Exhibitor activated' },
    { key: 'sendaccountinfo', label: 'Send account info' },
    { key: 'sendreminder', label: 'Send reminder' },
  ];

  logistics: LogisticsForm = {
    hotel: '',
    event_space: '',
    event_date: '',
    book_night: '',
    cancel_date: '',
    day1: '',
    day2: '',
  };

  media: MediaForm = {
    brand_logo: '',
    nav_background: '',
    hero_video_mp4: '',
    hero_video_webm: '',
    email_logo: '',
    pdf_logo: '',
    pdf_footer: '',
  };

  // Active languages are derived from the per-role main languages: emails, the
  // i18n editor and the lists all key off this deduped set (system, visitor,
  // exhibitor), so there is no separate "active languages" multiselect.
  get languages(): string[] {
    return Array.from(
      new Set([this.roleLanguages.system, this.roleLanguages.visitor, this.roleLanguages.exhibitor]),
    );
  }

  // Per-role main content language. Visitor pages are authored in the visitor
  // language, exhibitor pages in the exhibitor language; `system` is the
  // back-office/default fallback. Persisted in the `system` settings group.
  roleLanguages: { system: string; visitor: string; exhibitor: string } = {
    system: 'en',
    visitor: 'de',
    exhibitor: 'fr',
  };

  days: EventDay[] = [];

  // ---- Frontend client dates (assets/clients/<client>.json) ------------
  // The registration/profile forms read these from environment.* at boot, so
  // they live in the client JSON file (not event_settings). We edit them with
  // friendly fields here and persist by patching the full client JSON via the
  // Config-files API, keeping every other key intact. The legacy pages pick the
  // same values up through lc_frontend_config.inc.php.
  frontendClientId = '';
  private frontendClientRaw: { [key: string]: any } = {};
  frontendDatesLoaded = false;
  frontendDatesReadonly = false;
  frontendDatesMessage = '';
  frontendDates = {
    day_1: '',
    day_2: '',
    event_dates: '',
    event_date: '',
    book_night: '',
    cancel_date: '',
    arrival_day_before_date: '',
    lunch_dates: '', // comma-separated in the UI, stored as an array
    nimes_visit_label: '',
    inforeisen_trips: '', // comma-separated in the UI, stored as an array
  };
  /** PDF filename per Inforeise trip (index-aligned with parsed inforeisen_trips). */
  inforeisen_pdfs: string[] = [];

  // Welcome page body (client JSON key `txt_Welcome_Text`). Edited on the Event
  // tab with the inline rich editor and written back into the client JSON, which
  // the public welcome page (phuture-login) reads at boot.
  welcomeText = '';

  // ---- Content (Phase 2 — WYSIWYG) -------------------------------------
  // Single ordered list of editable content pages. `prose` sections render one
  // rich-text editor per active language; `program` sections render a tab
  // builder per active language (stored as JSON). The Content tab renders one
  // sub-tab per section, in this order.
  readonly contentSections: ContentSection[] = [
    { key: 'login_welcome', label: 'Login welcome', kind: 'prose', role: 'visitor' },
    { key: 'exexhibitors', label: 'Exhibitors intro', kind: 'prose', role: 'exhibitor' },
    { key: 'vvisitors', label: 'Visitors intro', kind: 'prose', role: 'visitor' },
    { key: 'vprogram', label: 'Program — visitor', kind: 'program', role: 'visitor' },
    { key: 'exprogram', label: 'Program — exhibitor', kind: 'program', role: 'exhibitor' },
    { key: 'vpartners', label: 'Partners — visitor', kind: 'prose', role: 'visitor' },
    { key: 'expartners', label: 'Partners — exhibitor', kind: 'prose', role: 'exhibitor' },
    { key: 'vcontact', label: 'Contact — visitor', kind: 'prose', role: 'visitor' },
    { key: 'excontact', label: 'Contact — exhibitor', kind: 'prose', role: 'exhibitor' },
  ];

  // Prose blocks: one rich-text editor in the page's role language.
  get proseBlocks(): ContentSection[] {
    return this.contentSections.filter((s) => s.kind === 'prose');
  }
  // Program blocks: a tab builder in the page's role language, stored as JSON.
  get programBlocks(): ContentSection[] {
    return this.contentSections.filter((s) => s.kind === 'program');
  }

  /** The single language a content page is authored/served in, by its role. */
  sectionLang(section: ContentSection): string {
    return this.roleLanguages[section.role] || this.roleLanguages.system || 'en';
  }

  /** Friendly caption like "Exhibitor — Français (fr)" for a section editor. */
  sectionLangLabel(section: ContentSection): string {
    const role = section.role.charAt(0).toUpperCase() + section.role.slice(1);
    const code = this.sectionLang(section);
    return `${role} — ${this.langLabel(code)} (${code})`;
  }

  /**
   * The single language an email template is authored in, chosen by the
   * recipient role via the System tab (visitor/exhibitor/system). This mirrors
   * what the PHP senders request (lc_email_lang), so the admin only edits the
   * language that is actually used at send time.
   */
  emailLang(block: { role: ContentRole }): string {
    return this.roleLanguages[block.role] || this.roleLanguages.system || 'en';
  }

  /** Single-element list of the email's role language, for *ngFor in the template. */
  emailLangs(block: { role: ContentRole }): string[] {
    return [this.emailLang(block)];
  }

  // prose[blockKey][lang] = { title, html }
  prose: { [key: string]: { [lang: string]: ContentBlock } } = {};
  // program[blockKey][lang] = ProgramTab[]
  program: { [key: string]: { [lang: string]: ProgramTab[] } } = {};

  // ---- Email templates (Phase 3) ---------------------------------------
  // Each transactional email is authored in a single language, chosen by the
  // recipient `role` (visitor/exhibitor) via the System tab's role_languages;
  // `system` covers the back-office/admin mails. `tokens` are the
  // {{ placeholders }} the sender fills at send time; an empty template falls
  // back to the built-in design.
  readonly emailBlocks: { key: string; label: string; role: ContentRole; tokens: string[] }[] = [
    {
      key: 'send-account-info',
      role: 'visitor',
      label: 'Account info / credentials (sent to participant)',
      tokens: [
        'salutation',
        'event_title',
        'event_domain',
        'validate_registration_latest',
        'username',
        'password',
      ],
    },
    {
      key: 'change-pwd',
      role: 'system',
      label: 'Password changed',
      tokens: ['first_name', 'last_name', 'event_title', 'email'],
    },
    {
      key: 'contact',
      role: 'system',
      label: 'Contact form',
      tokens: ['subject', 'message', 'first_name', 'last_name', 'userid', 'phone', 'email', 'event_title'],
    },
    {
      key: 'approve-visitor',
      role: 'visitor',
      label: 'Visitor registration complete (credentials)',
      tokens: ['salutation', 'event_title', 'event_domain', 'username', 'password'],
    },
    {
      key: 'approve-exhibitor',
      role: 'exhibitor',
      label: 'Exhibitor registration complete (credentials)',
      tokens: ['salutation', 'event_title', 'event_domain', 'username', 'password'],
    },
    {
      key: 'send-reminder',
      role: 'visitor',
      label: 'Reminder: complete profile (visitor)',
      tokens: [
        'salutation',
        'event_title',
        'event_domain',
        'validate_registration_latest',
        'username',
        'password',
      ],
    },
    {
      key: 'send-reminder-exhibitor',
      role: 'exhibitor',
      label: 'Reminder: complete profile (exhibitor)',
      tokens: [
        'salutation',
        'event_title',
        'event_domain',
        'complete_profile_by',
        'username',
        'password',
      ],
    },
    {
      key: 'send-account-info-exhibitor',
      role: 'exhibitor',
      label: 'Account info / credentials (exhibitor)',
      tokens: [
        'salutation',
        'event_title',
        'event_domain',
        'complete_profile_by',
        'username',
        'password',
      ],
    },
    {
      key: 'create-appointment-visitor',
      role: 'visitor',
      label: 'Appointment created (to visitor)',
      tokens: [
        'visitor_name',
        'exhibitor_name',
        'exhibitor_company',
        'visitor_company',
        'appointment_time',
        'city',
        'info',
      ],
    },
    {
      key: 'create-appointment-exhibitor',
      role: 'exhibitor',
      label: 'Appointment created (to exhibitor)',
      tokens: [
        'visitor_name',
        'exhibitor_name',
        'exhibitor_company',
        'visitor_company',
        'appointment_time',
        'city',
        'info',
      ],
    },
    {
      key: 'cancel-appointment-visitor',
      role: 'visitor',
      label: 'Appointment cancelled (to visitor)',
      tokens: [
        'visitor_name',
        'exhibitor_name',
        'exhibitor_company',
        'visitor_company',
        'appointment_time',
        'city',
        'reason',
      ],
    },
    {
      key: 'cancel-appointment-exhibitor',
      role: 'exhibitor',
      label: 'Appointment cancelled (to exhibitor)',
      tokens: [
        'visitor_name',
        'exhibitor_name',
        'exhibitor_company',
        'visitor_company',
        'appointment_time',
        'city',
        'reason',
      ],
    },
    {
      key: 'register-new-request',
      role: 'system',
      label: 'New registration request (admin, legacy form)',
      tokens: [
        'company',
        'address',
        'city',
        'country',
        'company_phone',
        'company_mail',
        'website',
        'title',
        'last_name',
        'first_name',
        'position',
        'contact_phone',
        'contact_mail',
      ],
    },
    {
      key: 'forgot-username',
      role: 'system',
      label: 'Forgot username / identification data (admin)',
      tokens: ['email', 'name', 'username', 'password', 'user_type'],
    },
  ];
  // emails[templateKey][lang] = { subject, body_html }
  emails: { [key: string]: { [lang: string]: EmailTemplate } } = {};

  // Active sub-tab index for the Content / E-mail sections. The horizontal
  // sub-tab header is replaced by a dropdown bound to these (see template),
  // so navigating many pages/emails stays compact.
  contentTabIndex = 0;
  emailTabIndex = 0;

  // Literal {{ token }} examples shown in the UI. Kept as plain strings here so
  // the template never has to escape double braces (which breaks Angular's
  // interpolation lexer).
  readonly tokenExample = '{{ token }}';
  readonly subjectExample = 'Welcome to {{ event_title }}';

  /** Render a token name as its literal placeholder, e.g. "event_title" -> "{{ event_title }}". */
  token(name: string): string {
    return '{{ ' + name + ' }}';
  }

  // Self-hosted TinyMCE: resources are served from /tinymce (see angular.json),
  // and embedded images are uploaded through the superuser asset endpoint.
  editorInit: any = {
    base_url: '/tinymce',
    suffix: '.min',
    height: 360,
    menubar: false,
    branding: false,
    promotion: false,
    plugins: 'lists advlist link image table code',
    toolbar:
      'undo redo | blocks | bold italic underline | bullist numlist | link image browse table | removeformat code',
    images_upload_handler: (blobInfo: any) =>
      new Promise<string>((resolve, reject) => {
        this.configService.uploadContentImage(blobInfo.blob(), blobInfo.filename()).subscribe({
          next: (rel) => resolve(`${this.configService.fileDbBaseUrl}/${rel}`),
          error: () => reject('Image upload failed'),
        });
      }),
    // Custom "Browse" button: pick a previously uploaded image (mirrors the
    // studema Flmngr feature, built natively for the self-hosted editor).
    setup: (editor: any) => {
      editor.ui.registry.addButton('browse', {
        text: 'Browse',
        tooltip: 'Browse uploaded images',
        onAction: () => this.openImagePicker(editor),
      });
    },
    // Make the editor iframe render content the same way the live eplus pages
    // do: load the Tailwind Play CDN (same dependency as eplus/_inc/meta.inc)
    // plus the page's small custom style overrides, so what you author looks
    // like the published page instead of unstyled HTML.
    init_instance_callback: (editor: any) => {
      try {
        const doc: Document = editor.getDoc();
        if (!doc || doc.getElementById('ec-tw-cdn')) return;
        const head = doc.head || doc.getElementsByTagName('head')[0];
        const script = doc.createElement('script');
        script.id = 'ec-tw-cdn';
        script.src = 'https://cdn.tailwindcss.com';
        head.appendChild(script);
        const style = doc.createElement('style');
        style.textContent = EDITOR_CONTENT_OVERRIDES;
        head.appendChild(style);
      } catch {
        // Best-effort styling; the editor still works if injection fails.
      }
    },
  };

  // Compact editor for the Legal tab: only the formatting the consent texts
  // need (bold / italic / link), no images/tables/blocks. Self-hosted TinyMCE.
  legalEditorInit: any = {
    base_url: '/tinymce',
    suffix: '.min',
    height: 200,
    menubar: false,
    branding: false,
    promotion: false,
    plugins: 'link',
    toolbar: 'bold italic | link | removeformat',
    // Keep the pasted/authored markup lean so it renders cleanly inside the
    // registration form labels.
    valid_elements: 'p,br,strong/b,em/i,a[href|target|rel],ul,ol,li,span',
  };

  // ---- Legal tab: per-role consent texts (Phase: Legal) ----------------
  // legalText[role][type][lang] = html. Stored in event_content under block
  // keys `legal_<role>_<type>`; the public registration forms read the saved
  // value at boot (falling back to their own inline markup when empty).
  readonly legalSections: LegalSectionDef[] = LEGAL_SECTIONS;
  legalText: { [role: string]: { [type: string]: { [lang: string]: { html: string } } } } = {};
  legalSaving = false;
  legalStatus = '';
  legalStatusError = false;

  // Stable groups for the template *ngFor. Computed once so the editors are not
  // destroyed/recreated on every change-detection cycle.
  readonly legalGroups: { role: LegalRole; label: string; sections: LegalSectionDef[] }[] = [
    { role: 'visitor', label: 'Visitor', sections: LEGAL_SECTIONS.filter((s) => s.role === 'visitor') },
    { role: 'exhibitor', label: 'Exhibitor', sections: LEGAL_SECTIONS.filter((s) => s.role === 'exhibitor') },
  ];

  // Selected Visitor/Exhibitor sub-tab inside the Legal tab.
  legalGroupIndex = 0;

  // Per legal type: a color "tone" so each consent section is visually distinct
  // (the CSS class adds a left accent border + tinted header in the template).
  private readonly legalTones: { [type in LegalType]: string } = {
    terms: 'sky',
    storno: 'amber',
    dsgvo: 'violet',
    media: 'emerald',
    haftung: 'rose',
  };

  /** CSS tone keyword for a legal section card (see .ec-legal-card--* in scss). */
  legalTone(type: LegalType): string {
    return this.legalTones[type] || 'sky';
  }

  /** The single language a role's legal texts are authored/served in. */
  legalLang(role: LegalRole): string {
    return this.roleLanguages[role] || this.roleLanguages.system || 'en';
  }

  /** Single-element list of the role's legal language, for *ngFor in template. */
  legalLangs(role: LegalRole): string[] {
    return [this.legalLang(role)];
  }

  /** Friendly caption like "Visitor language: Deutsch (de)". */
  legalLangLabel(role: LegalRole): string {
    const code = this.legalLang(role);
    return `${this.langLabel(code)} (${code})`;
  }

  // ---- Stage 5B: structured lists --------------------------------------
  // Activities are language-dependent (one column per active language, rows
  // aligned by index). Exhibitor categories are a single language-neutral list.
  activitiesByLang: { [lang: string]: string[] } = {};
  categories: string[] = [];

  // ---- Shared desk pairs -----------------------------------------------
  // Two exhibitors from different companies that share one physical desk and
  // therefore one agenda: booking a slot with either one mirrors the meeting
  // onto the other. Stored in the 'exhibitor_desk_pairs' settings group as
  // { pairs: [ { a, b }, ... ] }. The legacy booking helpers (getDeskPartner)
  // read the same group. `exhibitorOptions` backs the two dropdowns per row.
  deskPairs: { a: number; b: number }[] = [];
  exhibitorOptions: { id: number; label: string }[] = [];
  deskPairsMessage = '';

  // ---- Phase 7: per-language UI wording (event_i18n) -------------------
  // Editable rows of { key, value-per-language }. Empty values are not stored
  // (the consumer falls back to its built-in wording for that key).
  i18nRows: I18nRow[] = [];

  // ---- Phase 6: event photo gallery ------------------------------------
  galleryImages: GalleryImage[] = [];
  galleryCategories: string[] = [];
  galleryCategory = 'general';
  galleryMessage = '';
  pondGalleryOptions = this.buildGalleryPondOptions();
  pondGalleryHeroOptions = this.buildGalleryHeroPondOptions();

  // Editable gallery metadata (hero, intro, per-category title/cover/order).
  galleryMeta: GalleryMeta = { hero: '', intro: '', categories: [] };

  // ---- Stage 5D: raw config-file editor --------------------------------
  configFiles: ConfigFileMeta[] = [];
  selectedFileId = '';
  fileContent = '';
  fileReadonly = false;
  fileType = '';
  fileError = '';
  fileMessage = '';
  // Editor font size in px (A- / A+ buttons), clamped to a sensible range.
  fileFontSize = 11;

  // Inline documentation for the known event config files, shown below the
  // editor so admins understand what each file does and how it is structured.
  readonly configFileHelp: {
    [id: string]: { title: string; description: string; example: string };
  } = {
    'event/overrides': {
      title: 'Event overrides',
      description:
        'Free-form JSON for event values that are not yet modelled in the other tabs. ' +
        'Structured settings (event, logistics, media, languages, agenda…) live in the database ' +
        'and are edited from the dedicated tabs — use this file only as a staging area for ad-hoc ' +
        'values. It is safe to read from the API/legacy as plain JSON. Note: keys placed here are ' +
        'not applied automatically anywhere yet; a developer wires a specific key in where it is needed.',
      example: `{
  "overrides": {
    "show_countdown": true,
    "registration_deadline": "2026-09-01",
    "max_meetings_per_visitor": 12
  }
}`,
    },
    'event/booking-restrictions': {
      title: 'Booking restrictions',
      description:
        'Per-visitor booking blackout slots, overlaid onto the event agenda at runtime. ' +
        'Set "enabled": true to activate (false = full availability). Under "cities", the key is the ' +
        'event day/city id; "na_ids" is a comma list of visitor ids that may not be booked, and ' +
        '"na_time"[visitorId] is the comma list of time slots blocked for that visitor.',
      example: `{
  "enabled": true,
  "cities": {
    "2": {
      "na_caption": "--",
      "na_ids": "23, 24, 101",
      "na_time": {
        "23": "09:00, 09:15, 09:30",
        "101": "13:30, 13:45"
      }
    }
  }
}`,
    },
    'event/dynamic-fields': {
      title: 'Dynamic profile fields',
      description:
        'Optional visitor/exhibitor profile fields, overlaid onto the conf $show_dyn_* / $caption_dyn_* ' +
        'globals at runtime. For each group: "show" is a list of booleans (one per caption, in order) ' +
        'that toggles each field on/off; "captions" maps the DB column key to its label. ' +
        'An empty or absent file keeps the conf defaults.',
      example: `{
  "visitor": {
    "products": {
      "show": [true, false, true],
      "captions": {
        "pobusiness": "Business",
        "poreligious": "Religious tourism",
        "poyouth": "Youth tourism"
      }
    }
  }
}`,
    },
    'frontend/app-config': {
      title: 'Frontend app config',
      description:
        'The Angular app bootstrap file (assets/config.json). Selects the active client JSON and the ' +
        'deployment phase flags. Read at every page load by RuntimeConfigService, so changes take effect ' +
        'on the next browser reload — no rebuild needed for the main SPA. These flags win over the ' +
        'client JSON for any overlapping key.',
      example: `{
  "client": "bonjour-frankreich",
  "event_finish": false,
  "show_teaser": false,
  "show_gallery": false,
  "showGalleryAlways": false
}`,
    },
    // Generic help for any "frontend/<client>" entry (ids are dynamic, so the
    // getter falls back to this for every frontend client file except app-config).
    'frontend/*': {
      title: 'Frontend client config',
      description:
        'The Angular runtime config for one client (assets/clients/<client>.json). This is the canonical ' +
        'non-secret source: the SPA loads it at boot into environment.*, and the legacy eplus pages read ' +
        'the same file via the frontend-config bridge. Edits take effect on the next browser/page reload ' +
        'with no rebuild. NOTE: the embedded exhibitor/login widgets bake their config at build time and ' +
        'are NOT affected by edits here — those still require a widget rebuild.',
      example: `{
  "client": "bonjour-frankreich",
  "arrival_day_before_date": "29.09.2026",
  "lunch_dates": ["02.10.2026"],
  "nimes_visit_label": "Je participe à une visite guidée de Nîmes …",
  "inforeisen_trips": ["Inforeise 1", "Inforeise 2", "Inforeise 3"],
  "inforeisen_pdfs": ["inforeise-1.pdf", "inforeise-2.pdf", "inforeise-3.pdf"]
}`,
    },
  };

  get currentFileHelp(): { title: string; description: string; example: string } | null {
    if (this.configFileHelp[this.selectedFileId]) {
      return this.configFileHelp[this.selectedFileId];
    }
    // Any "frontend/<client>" file (dynamic id) shares the generic client help.
    if (this.selectedFileId.indexOf('frontend/') === 0) {
      return this.configFileHelp['frontend/*'];
    }
    return null;
  }

  /** Editable config files (writable JSON), shown first in the list. */
  get writableConfigFiles(): ConfigFileMeta[] {
    return this.configFiles.filter((f) => !f.readonly);
  }

  /** View-only config files (secrets / server-edited), shown below. */
  get readonlyConfigFiles(): ConfigFileMeta[] {
    return this.configFiles.filter((f) => f.readonly);
  }

  // ---- Admin user management (superuser-only tab) ----------------------
  // Manages plain backend admins (t_beuser, beusergroup_id = 1). Superusers
  // are listed read-only (su badge) but never edited/deactivated here.
  adminUsers: AdminUser[] = [];
  adminEditing = false;
  adminMessage = '';
  adminError = '';
  adminForm: {
    id?: number;
    name: string;
    login: string;
    email: string;
    company: string;
    password: string;
    active: number;
  } = { name: '', login: '', email: '', company: '', password: '', active: 1 };

  // FilePond options for the admin avatar uploader (built lazily once the
  // acting + target ids are known). Re-reads the editing id at send time.
  pondAdminAvatarOptions = this.buildAdminAvatarPondOptions();

  /** True when the acting backend user is a superuser (gates the Admins tab). */
  get isSuperuser(): boolean {
    return this.authQuery.isSuperuser;
  }

  // ---- Image picker (browse uploaded images) ---------------------------
  pickerOpen = false;
  pickerLoading = false;
  pickerImages: ContentImage[] = [];
  pondPickerOptions = this.buildPickerPondOptions();
  private activeEditor: any = null;

  // FilePond
  pondImageOptions = this.buildPondOptions('image', ['image/png', 'image/jpeg', 'image/webp'], 'JPG, PNG, WebP');
  pondVideoOptions = this.buildPondOptions('video', ['video/mp4', 'video/webm'], 'MP4, WebM');
  pondInforeisePdfOptions = this.buildInforeisePondOptions();

  constructor(
    private configService: EventConfigService,
    private authQuery: AuthQuery,
    private ngZone: NgZone,
    private exhibitorService: ExhibitorService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.configService.getConfig().subscribe({
      next: (res) => {
        this.client = res.client;
        const s = res.settings || {};
        const d = res.defaults;

        this.event = { ...this.event, ...d.event, ...(s['event'] || {}) };
        this.logistics = { ...this.logistics, ...d.logistics, ...(s['logistics'] || {}) };
        this.media = { ...this.media, ...(s['media'] || {}) };
        this.flags = { ...this.flags, ...(s['flags'] || {}) };
        this.notifications = { ...this.notifications, ...(s['notifications'] || {}) };
        this.testmail = { ...this.testmail, ...(s['testmail'] || {}) };
        const sys = s['system'] || {};
        const rl = sys.role_languages || {};
        const fallback = this.effectiveClient === 'french-rendez-vous' ? 'en' : 'de';
        this.roleLanguages = {
          system: rl.system || 'en',
          visitor: rl.visitor || fallback,
          exhibitor: rl.exhibitor || 'fr',
        };
        this.applyLists(s);
        this.applyDeskPairs(s);
        this.days = (res.days || []).slice();

        // current lifecycle phase (stored as a scalar ckey); default keeps the
        // header dot green until a phase is explicitly chosen and saved.
        const storedPhase = s['phase'];
        this.activePhase =
          (typeof storedPhase === 'string' && storedPhase) ||
          (storedPhase && storedPhase.current) ||
          DEFAULT_PHASE_ID;

        this.loading = false;
        this.loadContent();
        this.loadEmails();
        this.loadConfigFiles();
        this.loadFrontendDates();
        this.loadI18n();
        this.loadGallery();
        this.loadExhibitorOptions();
        if (this.isSuperuser) this.loadAdminUsers();
        if (this.isSuperuser) this.loadSysInfo();
      },
      error: () => {
        this.message = 'Could not load configuration (superuser required).';
        this.loading = false;
      },
    });
  }

  // ---- Sysadmin tab --------------------------------------------------

  loadSysInfo(): void {
    this.sysInfoLoading = true;
    this.sysInfoError = '';
    this.configService.getSysInfo().subscribe({
      next: (res) => {
        this.sysInfo = res;
        this.sysInfoLoading = false;
      },
      error: () => {
        this.sysInfoError = 'Could not load system information (superuser required).';
        this.sysInfoLoading = false;
      },
    });
  }

  runMigration(): void {
    if (this.migrating) return;
    this.migrating = true;
    this.migrationResult = null;
    this.migrationError = '';
    this.configService.runMigration().subscribe({
      next: (res) => {
        this.migrationResult = res.result;
        if (res.result && res.result.ok === false) {
          this.migrationError = res.result.error || 'Migration failed.';
        }
        this.migrating = false;
        // Refresh the snapshot so schema version / column presence update.
        this.loadSysInfo();
      },
      error: () => {
        this.migrationError = 'Migration request failed (superuser required or server error).';
        this.migrating = false;
      },
    });
  }

  loadContent(): void {
    this.configService.getContent().subscribe({
      next: (res) => this.applyContent(res.blocks || {}),
      error: () => {},
    });
  }

  private applyContent(blocks: { [k: string]: { [lang: string]: ContentBlock } }): void {
    // Only the page's role language is seeded/edited (one editor per page).
    for (const b of this.proseBlocks) {
      this.prose[b.key] = this.prose[b.key] || {};
      const lang = this.sectionLang(b);
      const stored = blocks[b.key] && blocks[b.key][lang];
      // Seed the editor with the page's built-in content when nothing is
      // stored yet, so the admin edits from the current page rather than blank.
      const html = stored && stored.html ? stored.html : CONTENT_DEFAULTS.prose[b.key] || '';
      this.prose[b.key][lang] = { title: (stored && stored.title) || '', html };
    }
    for (const b of this.programBlocks) {
      this.program[b.key] = this.program[b.key] || {};
      const lang = this.sectionLang(b);
      let tabs: ProgramTab[] = [];
      const stored = blocks[b.key] && blocks[b.key][lang];
      if (stored && stored.html) {
        try {
          const parsed = JSON.parse(stored.html);
          if (parsed && Array.isArray(parsed.tabs)) {
            // Accept both the new structured shape and the legacy {title,html}.
            tabs = parsed.tabs.map((t: any) => this.normalizeTab(t));
          }
        } catch {
          // not JSON — ignore, fall through to the built-in default below
        }
      }
      // Seed with the page's built-in program tabs when nothing is stored yet,
      // converting the default HTML bodies into structured agenda items.
      if (tabs.length === 0) {
        tabs = (CONTENT_DEFAULTS.program[b.key] || []).map((t) => this.normalizeTab(t));
      }
      this.program[b.key][lang] = tabs;
      this.programDay[b.key] = 0;
    }
    this.applyLegal(blocks);
  }

  /**
   * Seed the Legal tab editors from the stored `legal_<role>_<type>` blocks.
   * Each role is authored only in its configured language (visitor/exhibitor),
   * so we seed that one language; empty/missing blocks fall back to the built-in
   * wording so the admin starts from the form's current text. Texts stored in
   * other languages are left untouched in the DB (saveLegal never sends them).
   */
  private applyLegal(blocks: { [k: string]: { [lang: string]: ContentBlock } }): void {
    for (const s of this.legalSections) {
      this.legalText[s.role] = this.legalText[s.role] || {};
      this.legalText[s.role][s.type] = this.legalText[s.role][s.type] || {};
      const blockKey = this.legalBlockKey(s.role, s.type);
      const lang = this.legalLang(s.role);
      const stored = blocks[blockKey] && blocks[blockKey][lang];
      const html = stored && stored.html ? stored.html : legalDefault(s.role, s.type, lang);
      this.legalText[s.role][s.type][lang] = { html };
    }
  }

  loadEmails(): void {
    this.configService.getEmailTemplates().subscribe({
      next: (res) => {
        const templates = res.templates || {};
        this.applyEmails(templates);
        this.seedMissingEmails(templates);
      },
      error: () => {},
    });
  }

  /**
   * Ensure every default email template has a DB row so the legacy senders use
   * the dynamic template (and brand-logo wrapper) instead of their hardcoded
   * fallbacks. Only (key, lang) pairs that are missing or have an empty body in
   * the current DB are sent; existing edits are preserved server-side. Runs once
   * when the E-mail tab data loads and is idempotent.
   */
  private seedMissingEmails(stored: { [k: string]: { [lang: string]: EmailTemplate } }): void {
    const missing: SaveEmailTemplate[] = [];
    for (const key of Object.keys(EMAIL_DEFAULTS)) {
      const byLang = EMAIL_DEFAULTS[key];
      for (const lang of Object.keys(byLang)) {
        const def = byLang[lang];
        if (!def || !(def.body_html || '').trim()) continue;
        const row = stored[key] && stored[key][lang];
        if (row && (row.body_html || '').trim()) continue;
        missing.push({
          template_key: key,
          lang,
          subject: def.subject || '',
          body_html: def.body_html || '',
        });
      }
    }
    if (missing.length === 0) return;
    this.configService.seedEmailTemplates(missing).subscribe({ next: () => {}, error: () => {} });
  }

  private applyEmails(templates: { [k: string]: { [lang: string]: EmailTemplate } }): void {
    for (const b of this.emailBlocks) {
      this.emails[b.key] = this.emails[b.key] || {};
      // Each email is authored only in its role's language (visitor/exhibitor/
      // system). A stored row in another language is still preserved untouched
      // because we never clear it here; we only load/seed the active one.
      const lang = this.emailLang(b);
      const stored = templates[b.key] && templates[b.key][lang];
      // Pre-fill empty templates with the built-in seed for that language so
      // the editor shows the email's current wording (with {{ tokens }}).
      const def = (EMAIL_DEFAULTS[b.key] && EMAIL_DEFAULTS[b.key][lang]) || { subject: '', body_html: '' };
      this.emails[b.key][lang] = {
        subject: (stored && stored.subject) || def.subject || '',
        body_html: (stored && stored.body_html) || def.body_html || '',
      };
    }
  }

  emailModel(key: string, lang: string): EmailTemplate {
    if (!this.emails[key]) this.emails[key] = {};
    if (!this.emails[key][lang]) this.emails[key][lang] = { subject: '', body_html: '' };
    return this.emails[key][lang];
  }

  // ---- Phase 7: languages (event_i18n) ---------------------------------

  loadI18n(): void {
    this.configService.getI18n().subscribe({
      next: (res) => this.applyI18n(res.i18n || {}),
      error: () => this.applyI18n({}),
    });
  }

  private applyI18n(data: { [lang: string]: { [key: string]: string } }): void {
    // Union of keys across all languages; seed with the example keys when the
    // table is still empty so the editor opens with a starting set.
    const keys = new Set<string>();
    for (const lang of Object.keys(data)) {
      for (const k of Object.keys(data[lang] || {})) keys.add(k);
    }
    if (keys.size === 0) {
      for (const lang of Object.keys(I18N_DEFAULTS)) {
        for (const k of Object.keys(I18N_DEFAULTS[lang])) keys.add(k);
      }
    }

    this.i18nRows = Array.from(keys).map((key) => {
      const values: { [lang: string]: string } = {};
      for (const lang of this.languages) {
        const stored = data[lang] && data[lang][key];
        const seed = I18N_DEFAULTS[lang] && I18N_DEFAULTS[lang][key];
        values[lang] = stored || seed || '';
      }
      return { key, values };
    });
  }

  /** Lazily ensure a row has a slot for the language (e.g. after adding one). */
  i18nValue(row: I18nRow, lang: string): string {
    if (row.values[lang] === undefined) row.values[lang] = '';
    return row.values[lang];
  }

  setI18nValue(row: I18nRow, lang: string, value: string): void {
    row.values[lang] = value;
  }

  addI18nRow(): void {
    const values: { [lang: string]: string } = {};
    for (const lang of this.languages) values[lang] = '';
    this.i18nRows.push({ key: '', values });
  }

  removeI18nRow(index: number): void {
    this.i18nRows.splice(index, 1);
  }

  saveI18n(): void {
    const entries: SaveI18nEntry[] = [];
    const seen = new Set<string>();
    for (const row of this.i18nRows) {
      const key = (row.key || '').trim();
      if (!key || seen.has(key)) continue; // skip blank / duplicate keys
      seen.add(key);
      for (const lang of this.languages) {
        entries.push({ lang, ikey: key, value: (row.values[lang] || '').trim() });
      }
    }
    this.configService.saveI18n(entries).subscribe(() => this.flash('Languages saved.'));
  }

  // ---- Phase 6: gallery -------------------------------------------------

  loadGallery(): void {
    this.configService.getGallery().subscribe({
      next: (res) => {
        this.galleryImages = res.images || [];
        this.galleryCategories = res.categories || [];
        this.syncGalleryCategoriesIntoMeta();
      },
      error: () => {
        this.galleryImages = [];
        this.galleryCategories = [];
      },
    });
    this.loadGalleryMeta();
  }

  loadGalleryMeta(): void {
    this.configService.getGalleryMeta().subscribe({
      next: (res) => {
        const meta = res.meta || { hero: '', intro: '', categories: [] };
        this.galleryMeta = {
          hero: meta.hero || '',
          intro: meta.intro || '',
          categories: Array.isArray(meta.categories) ? meta.categories : [],
        };
        this.syncGalleryCategoriesIntoMeta();
      },
      error: () => {
        this.galleryMeta = { hero: '', intro: '', categories: [] };
      },
    });
  }

  /**
   * Make sure every folder that has images is represented in the metadata
   * editor, and keep entries ordered. Categories discovered on disk but not yet
   * in the metadata are appended with a title-cased default.
   */
  private syncGalleryCategoriesIntoMeta(): void {
    const existing = new Set(this.galleryMeta.categories.map((c) => c.slug));
    let order = this.galleryMeta.categories.length;
    for (const slug of this.galleryCategories) {
      if (!existing.has(slug)) {
        order++;
        this.galleryMeta.categories.push({
          slug,
          title: this.titleCase(slug),
          description: '',
          caption: '',
          cover: '',
          order,
        });
        existing.add(slug);
      }
    }
    this.sortGalleryCategories();
  }

  private sortGalleryCategories(): void {
    this.galleryMeta.categories.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  private titleCase(slug: string): string {
    return (slug || '')
      .replace(/[-_]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }

  /** Images filtered to the currently selected upload category. */
  get galleryImagesInCategory(): GalleryImage[] {
    const cat = (this.galleryCategory || '').trim();
    if (!cat) return this.galleryImages;
    return this.galleryImages.filter((img) => img.category === cat);
  }

  /** Display URL — prefers the generated thumbnail, falls back to the full image. */
  galleryUrl(img: GalleryImage): string {
    return `${this.configService.fileDbBaseUrl}/${img.thumb || img.path}`;
  }

  /** URL for an arbitrary _fileDB-relative path (cover/hero previews). */
  galleryPathUrl(path: string): string {
    if (!path) return '';
    return `${this.configService.fileDbBaseUrl}/${path}`;
  }

  onGalleryUploaded(): void {
    // a file finished uploading — refresh the grid (and category list)
    this.loadGallery();
  }

  onGalleryHeroUploaded(event: any): void {
    const serverId = event?.file?.serverId;
    if (serverId) {
      this.galleryMeta.hero = serverId;
      this.flash('Hero image uploaded — remember to Save gallery.');
    }
  }

  deleteGalleryImage(img: GalleryImage): void {
    this.openConfirm({
      title: 'Delete this image?',
      message: `“${img.name}” will be permanently removed from the gallery. This action cannot be undone.`,
      confirmLabel: 'Delete image',
      tone: 'danger',
      imageUrl: this.galleryUrl(img),
      onConfirm: () => this.performDeleteGalleryImage(img),
    });
  }

  private performDeleteGalleryImage(img: GalleryImage): void {
    this.configService.deleteGalleryImage(img.path).subscribe(() => {
      this.galleryImages = this.galleryImages.filter((i) => i.path !== img.path);
      this.flash('Image deleted.');
    });
  }

  // ---- gallery metadata editing ----------------------------------------

  /** Pick the upload/edit category by slug (used when clicking an album row). */
  selectGalleryCategory(slug: string): void {
    this.galleryCategory = slug;
  }

  /** Add a brand-new (empty) category from the typed upload-category field. */
  addGalleryCategory(): void {
    const slug = this.slugify(this.galleryCategory);
    if (!slug) {
      return;
    }
    if (!this.galleryMeta.categories.some((c) => c.slug === slug)) {
      this.galleryMeta.categories.push({
        slug,
        title: this.titleCase(slug),
        description: '',
        caption: '',
        cover: '',
        order: this.galleryMeta.categories.length + 1,
      });
      this.sortGalleryCategories();
    }
    this.galleryCategory = slug;
  }

  removeGalleryCategory(cat: GalleryCategoryMeta): void {
    const hasImages = this.galleryImages.some((i) => i.category === cat.slug);
    const msg = hasImages
      ? `Remove album "${cat.title}" from the gallery configuration? The uploaded images stay on disk and the folder reappears as an unnamed album until you delete its images.`
      : `Remove album "${cat.title}"?`;
    if (!confirm(msg)) {
      return;
    }
    this.galleryMeta.categories = this.galleryMeta.categories.filter((c) => c.slug !== cat.slug);
  }

  moveGalleryCategory(index: number, delta: number): void {
    const cats = this.galleryMeta.categories;
    const target = index + delta;
    if (target < 0 || target >= cats.length) {
      return;
    }
    [cats[index], cats[target]] = [cats[target], cats[index]];
    cats.forEach((c, i) => (c.order = i + 1));
  }

  /** Use one of an album's uploaded images as its cover. */
  setGalleryCover(cat: GalleryCategoryMeta, img: GalleryImage): void {
    cat.cover = img.path;
  }

  imagesForCategory(slug: string): GalleryImage[] {
    return this.galleryImages.filter((i) => i.category === slug);
  }

  saveGalleryMeta(): void {
    // normalize order to the displayed sequence before saving
    this.galleryMeta.categories.forEach((c, i) => (c.order = i + 1));
    this.configService.saveGalleryMeta(this.galleryMeta).subscribe({
      next: () => this.flash('Gallery saved.'),
      error: () => this.flash('Could not save gallery.'),
    });
  }

  private slugify(value: string): string {
    return (value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // ---- save handlers ----------------------------------------------------

  saveEvent(): void {
    // Welcome heading + body now live in the 'event' settings group, so a single
    // API save persists everything; the public welcome page reads them from the
    // API at boot (RuntimeConfigService), so edits show after a reload.
    this.configService
      .saveConfig({ event: this.event })
      .subscribe(() => this.flash('Event saved (reload the public app to see the welcome changes).'));
  }

  saveLogistics(): void {
    this.configService.saveConfig({ logistics: this.logistics }).subscribe(() => this.flash('Logistics saved.'));
  }

  // ---- Frontend client dates -------------------------------------------

  private joinList(v: any): string {
    return Array.isArray(v) ? v.join(', ') : v || '';
  }

  private splitList(v: string): string[] {
    return (v || '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  /** Trip names parsed from the comma-separated Logistics input. */
  inforeisenTripNames(): string[] {
    return this.splitList(this.frontendDates.inforeisen_trips);
  }

  /** Keep the PDF slot array aligned with the number of configured trips. */
  syncInforeisenPdfSlots(): void {
    this.inforeisen_pdfs = this.normalizeInforeisenPdfs(this.inforeisen_pdfs, this.inforeisenTripNames().length);
  }

  private normalizeInforeisenPdfs(pdfs: any, tripCount: number): string[] {
    const src = Array.isArray(pdfs) ? pdfs.map((s) => String(s ?? '').trim()) : [];
    const out: string[] = [];
    for (let i = 0; i < tripCount; i++) {
      out.push(src[i] || '');
    }
    return out;
  }

  inforeisePdfUrl(filename: string): string {
    const name = (filename || '').trim();
    if (!name) {
      return '';
    }
    return `./assets/inforeisen/${name}`;
  }

  onInforeisePdfUploaded(index: number, event: any): void {
    const serverId = event?.file?.serverId;
    if (serverId) {
      this.syncInforeisenPdfSlots();
      this.inforeisen_pdfs[index] = String(serverId).trim();
      this.saveFrontendDates('Inforeise PDF saved (reload the public app to take effect).');
    }
  }

  clearInforeisenPdf(index: number): void {
    this.syncInforeisenPdfSlots();
    this.inforeisen_pdfs[index] = '';
    this.saveFrontendDates('Inforeise PDF removed (reload the public app to take effect).');
  }

  /** Load the active frontend client JSON so its dates can be edited as fields. */
  loadFrontendDates(): void {
    this.configService.getConfigFile('frontend/app-config', true).subscribe({
      next: (appRes) => {
        let appCfg: { [key: string]: any } = {};
        try {
          appCfg = JSON.parse(appRes.content || '{}') || {};
        } catch {
          appCfg = {};
        }
        const fileClient = String(appCfg['client'] || '').trim();
        if (!fileClient) {
          this.frontendDatesLoaded = false;
          return;
        }
        this.loadFrontendClientFile('frontend/' + fileClient, fileClient);
      },
      error: () => {
        this.frontendDatesLoaded = false;
      },
    });
  }

  /** Read + bind one frontend client JSON (assets/clients/{name}.json). */
  private loadFrontendClientFile(id: string, fileClient: string): void {
    this.frontendClientId = id;
    this.configService.getConfigFile(id, true).subscribe({
      next: (res) => {
        this.frontendDatesReadonly = !!res.readonly;
        let cfg: { [key: string]: any } = {};
        try {
          cfg = JSON.parse(res.content || '{}') || {};
        } catch {
          cfg = {};
        }
        this.applyFrontendClientConfig(cfg);
        this.maybeMigrateInforeisenPdfsFromLegacyFile(fileClient);
      },
      error: () => {
        this.frontendDatesLoaded = false;
      },
    });
  }

  private applyFrontendClientConfig(cfg: { [key: string]: any }): void {
    this.frontendClientRaw = cfg;
    this.frontendDates = {
      day_1: cfg['day_1'] || '',
      day_2: cfg['day_2'] || '',
      event_dates: cfg['event_dates'] || '',
      event_date: cfg['event_date'] || '',
      book_night: cfg['book_night'] || '',
      cancel_date: cfg['cancel_date'] || '',
      arrival_day_before_date: cfg['arrival_day_before_date'] || '',
      lunch_dates: this.joinList(cfg['lunch_dates']),
      nimes_visit_label: cfg['nimes_visit_label'] || '',
      inforeisen_trips: this.joinList(cfg['inforeisen_trips']),
    };
    this.inforeisen_pdfs = this.normalizeInforeisenPdfs(
      cfg['inforeisen_pdfs'],
      this.splitList(this.frontendDates.inforeisen_trips).length
    );
    this.welcomeText = cfg['txt_Welcome_Text'] || '';
    this.frontendDatesLoaded = true;
  }

  /** PDFs were saved under frontend/{logicalClient} instead of config.json selector. */
  private maybeMigrateInforeisenPdfsFromLegacyFile(fileClient: string): void {
    const logicalClient = String(environment.client || '').trim();
    if (!logicalClient || logicalClient === fileClient || !this.inforeisenPdfsEmpty(this.inforeisen_pdfs)) {
      return;
    }
    const legacyId = 'frontend/' + logicalClient;
    this.configService.getConfigFile(legacyId, true).subscribe({
      next: (res) => {
        let legacyCfg: { [key: string]: any } = {};
        try {
          legacyCfg = JSON.parse(res.content || '{}') || {};
        } catch {
          return;
        }
        const tripCount = this.splitList(this.frontendDates.inforeisen_trips).length;
        const legacyPdfs = this.normalizeInforeisenPdfs(legacyCfg['inforeisen_pdfs'], tripCount);
        if (this.inforeisenPdfsEmpty(legacyPdfs)) {
          return;
        }
        this.inforeisen_pdfs = legacyPdfs;
        this.saveFrontendDates('Inforeise PDFs migrated to the active client config (reload the public app).');
      },
      error: () => {
        /* legacy wrongly-named file not present */
      },
    });
  }

  private inforeisenPdfsEmpty(pdfs: string[]): boolean {
    return !(pdfs || []).some((name) => !!(name || '').trim());
  }

  /** Patch the edited date fields back into the full client JSON and save it. */
  saveFrontendDates(successMessage?: string): void {
    if (this.frontendDatesReadonly || !this.frontendClientId) {
      return;
    }
    this.frontendDatesMessage = '';
    const cfg = { ...this.frontendClientRaw };
    cfg['day_1'] = this.frontendDates.day_1;
    cfg['day_2'] = this.frontendDates.day_2;
    cfg['event_dates'] = this.frontendDates.event_dates;
    cfg['event_date'] = this.frontendDates.event_date;
    cfg['book_night'] = this.frontendDates.book_night;
    cfg['cancel_date'] = this.frontendDates.cancel_date;
    cfg['arrival_day_before_date'] = this.frontendDates.arrival_day_before_date;
    cfg['lunch_dates'] = this.splitList(this.frontendDates.lunch_dates);
    cfg['nimes_visit_label'] = this.frontendDates.nimes_visit_label;
    cfg['inforeisen_trips'] = this.splitList(this.frontendDates.inforeisen_trips);
    cfg['inforeisen_pdfs'] = this.normalizeInforeisenPdfs(
      this.inforeisen_pdfs,
      this.splitList(this.frontendDates.inforeisen_trips).length
    );

    this.configService.saveConfigFile(this.frontendClientId, JSON.stringify(cfg, null, 2)).subscribe({
      next: () => {
        this.frontendClientRaw = cfg;
        this.flash(successMessage || 'Registration dates saved (reload the public app to take effect).');
      },
      error: (e) => {
        this.frontendDatesMessage = 'Could not save: ' + ((e && e.error && e.error.error) || 'unknown error');
      },
    });
  }

  saveMedia(successMessage?: string): void {
    this.configService.saveConfig({ media: this.media }).subscribe(() =>
      this.flash(successMessage || 'Media saved.')
    );
  }

  saveSystem(): void {
    this.configService
      .saveConfig({ system: { languages: this.languages, role_languages: this.roleLanguages } })
      .subscribe(() => this.flash('System saved.'));
  }

  saveOperations(): void {
    // `event` is sent whole so the mail subject / deadline texts persist
    // alongside the title/year already held in that group.
    this.configService
      .saveConfig({
        flags: this.flags,
        notifications: this.notifications,
        testmail: this.testmail,
        event: this.event,
      })
      .subscribe(() => this.flash('Operations saved.'));
  }

  // ---- Event phase bar --------------------------------------------------
  // Quick booking toggles, mapped onto the existing operational flags so the
  // phase panels reuse the same state the Operations tab manages.
  get visitorBookingActive(): boolean {
    return !this.flags.booking_stop;
  }
  set visitorBookingActive(active: boolean) {
    this.flags.booking_stop = !active;
  }

  get exhibitorBookingActive(): boolean {
    return !!this.flags.exhibitor_booking_active;
  }
  set exhibitorBookingActive(active: boolean) {
    this.flags.exhibitor_booking_active = active;
  }

  /** Stop all bookings and mark the event as live, then persist. */
  goLive(): void {
    this.flags.booking_stop = true;
    this.activePhase = 'live';
    this.savePhase();
  }

  /** Re-open bookings (used from the live panel) and persist. */
  reopenBooking(): void {
    this.flags.booking_stop = false;
    this.savePhase();
  }

  /** Jump the main tab group to the named tab for full management. */
  goToTab(label: string): void {
    const i = this.tabOrder.indexOf(label);
    if (i >= 0) this.mainTabIndex = i;
  }

  /** Persist the selected phase together with the operational flags. */
  savePhase(): void {
    this.configService
      .saveConfig({ phase: this.activePhase, flags: this.flags })
      .subscribe(() => {
        // Notify the dashboard header so the phase badge + Live System dot
        // update immediately, without requiring a page reload.
        this.configService.notifyPhaseChanged(this.activePhase);
        this.flash('Phase saved.');
        // The phase is the single control: push the derived runtime flags into
        // the SPA bootstrap file (assets/config.json) so routing follows it.
        this.applyPhaseToAppConfig(this.activePhase);
      });
  }

  /**
   * Derive the runtime deployment flags from the phase and write them into the
   * frontend app config (assets/config.json). `client` and `showGalleryAlways`
   * are preserved (deployment selector + manual post-event sub-choice). Silent
   * no-op when the app config is not reachable/writable on this deployment.
   */
  private applyPhaseToAppConfig(phaseId: string): void {
    const id = 'frontend/app-config';
    // Optional probe (see loadFrontendDates): silence the global error handler.
    this.configService.getConfigFile(id, true).subscribe({
      next: (res) => {
        if (!res || res.readonly) {
          return; // asset dir not writable from the API on this deployment
        }
        let cfg: { [key: string]: any } = {};
        try {
          cfg = JSON.parse(res.content || '{}') || {};
        } catch {
          return; // never overwrite a file we cannot parse
        }
        const flags = phaseRuntimeFlags(phaseId);
        cfg['show_teaser'] = flags.show_teaser;
        cfg['event_finish'] = flags.event_finish;
        cfg['show_gallery'] = flags.show_gallery;
        // client + showGalleryAlways intentionally left untouched.
        this.configService.saveConfigFile(id, JSON.stringify(cfg, null, 2)).subscribe({
          next: () => this.flash('Phase applied to app config (reload to take effect).'),
          error: () => {
            /* non-fatal: phase is saved in the DB regardless */
          },
        });
      },
      error: () => {
        /* app config entry not registered (webapp assets not beside the API) */
      },
    });
  }

  saveAgenda(): void {
    this.configService.saveDays(this.days).subscribe(() => this.flash('Agenda saved.'));
  }

  saveContent(): void {
    const blocks: SaveContentBlock[] = [];
    // Each page is authored in exactly one language (its role language).
    for (const b of this.proseBlocks) {
      const lang = this.sectionLang(b);
      const m = this.proseModel(b.key, lang);
      blocks.push({ block_key: b.key, lang, title: m.title || '', html: m.html || '' });
    }
    for (const b of this.programBlocks) {
      const lang = this.sectionLang(b);
      // Persist the structured agenda (day -> heading + items). The legacy PHP
      // renderer builds the <ol><li> markup from these.
      const tabs = this.programTabs(b.key, lang).map((t) => ({
        title: t.title || '',
        heading: t.heading || '',
        items: (t.items || []).map((it) => ({
          time: it.time || '',
          text: it.text || '',
        })),
      }));
      blocks.push({ block_key: b.key, lang, title: '', html: JSON.stringify({ tabs }) });
    }

    this.contentSaving = true;
    this.contentStatus = '';
    this.contentStatusError = false;

    this.configService.saveContent(blocks).subscribe({
      next: (res) => {
        this.contentSaving = false;
        this.contentStatusError = false;
        const saved = res && typeof res.saved === 'number' ? res.saved : blocks.length;
        this.contentStatus = `Saved ${saved} content block${saved === 1 ? '' : 's'}.`;
        this.flash('Content saved.');
        setTimeout(() => (this.contentStatus = ''), 5000);
      },
      error: (err) => {
        this.contentSaving = false;
        this.contentStatusError = true;
        const detail =
          err?.error?.error || err?.error?.message || err?.message || `HTTP ${err?.status ?? '?'}`;
        this.contentStatus = `Save failed: ${detail}`;
      },
    });
  }

  /** event_content block key for a legal consent block. */
  legalBlockKey(role: LegalRole, type: LegalType): string {
    return `legal_${role}_${type}`;
  }

  /** Two-way model accessor for one legal editor (lazily initialised). */
  legalModel(role: LegalRole, type: LegalType, lang: string): { html: string } {
    this.legalText[role] = this.legalText[role] || {};
    this.legalText[role][type] = this.legalText[role][type] || {};
    if (!this.legalText[role][type][lang]) {
      this.legalText[role][type][lang] = { html: legalDefault(role, type, lang) };
    }
    return this.legalText[role][type][lang];
  }

  /** Whether a legal block is shown in the public form (client-JSON flag). */
  legalShow(flag: string): boolean {
    return !!this.frontendClientRaw[flag];
  }
  setLegalShow(flag: string, value: boolean): void {
    this.frontendClientRaw[flag] = !!value;
  }

  /**
   * Persist the Legal tab: the consent texts (event_content) for each role's
   * configured language, plus the show/hide flags patched back into the
   * client JSON (where the registration forms read them from environment.* at
   * boot).
   */
  saveLegal(): void {
    const blocks: SaveContentBlock[] = [];
    for (const s of this.legalSections) {
      const blockKey = this.legalBlockKey(s.role, s.type);
      // Each role is edited only in its configured language; save just that one
      // so texts authored in other languages stay intact server-side.
      const lang = this.legalLang(s.role);
      const html = (this.legalText[s.role]?.[s.type]?.[lang]?.html ?? '').trim();
      blocks.push({ block_key: blockKey, lang, title: '', html });
    }

    this.legalSaving = true;
    this.legalStatus = '';
    this.legalStatusError = false;

    this.configService.saveContent(blocks).subscribe({
      next: () => {
        // Persist the toggle flags into the client JSON (best-effort: the file
        // may be read-only or unreachable on API-only deployments).
        this.saveLegalFlags();
        this.legalSaving = false;
        this.legalStatusError = false;
        this.legalStatus = 'Legal texts saved (reload the public app to take effect).';
        this.flash('Legal saved.');
        setTimeout(() => (this.legalStatus = ''), 5000);
      },
      error: (err) => {
        this.legalSaving = false;
        this.legalStatusError = true;
        const detail =
          err?.error?.error || err?.error?.message || err?.message || `HTTP ${err?.status ?? '?'}`;
        this.legalStatus = `Save failed: ${detail}`;
      },
    });
  }

  /** Patch the *_show_* flags into the full client JSON and save it. */
  private saveLegalFlags(): void {
    // Only patch when the full client JSON was actually loaded — otherwise
    // writing the (near-empty) frontendClientRaw would clobber the client file.
    if (this.frontendDatesReadonly || !this.frontendClientId || !this.frontendDatesLoaded) {
      return;
    }
    const cfg = { ...this.frontendClientRaw };
    for (const s of this.legalSections) {
      cfg[s.flag] = !!this.frontendClientRaw[s.flag];
    }
    this.configService.saveConfigFile(this.frontendClientId, JSON.stringify(cfg, null, 2)).subscribe({
      next: () => {
        this.frontendClientRaw = cfg;
      },
      error: () => {
        // Texts already saved; surface a soft hint that flags did not persist.
        this.legalStatus = 'Texts saved, but the show/hide flags could not be written to the client config.';
        this.legalStatusError = true;
      },
    });
  }

  saveEmails(): void {
    const templates: SaveEmailTemplate[] = [];
    for (const b of this.emailBlocks) {
      // Persist only the role language the email is authored in; this is the
      // one the PHP senders request at send time.
      const lang = this.emailLang(b);
      const m = this.emailModel(b.key, lang);
      templates.push({
        template_key: b.key,
        lang,
        subject: m.subject || '',
        body_html: m.body_html || '',
      });
    }
    this.configService.saveEmailTemplates(templates).subscribe(() => this.flash('Email templates saved.'));
  }

  // ---- content accessors (lazily initialise structures) ----------------

  proseModel(key: string, lang: string): ContentBlock {
    if (!this.prose[key]) this.prose[key] = {};
    if (!this.prose[key][lang]) this.prose[key][lang] = { title: '', html: '' };
    return this.prose[key][lang];
  }

  programTabs(key: string, lang: string): ProgramTab[] {
    if (!this.program[key]) this.program[key] = {};
    if (!this.program[key][lang]) this.program[key][lang] = [];
    return this.program[key][lang];
  }

  // Index of the currently-edited day per program section. Only the active
  // day's editors are mounted, which bounds the number of TinyMCE instances.
  programDay: { [key: string]: number } = {};

  activeProgramDay(key: string): number {
    const tabs = this.programTabs(key, this.sectionLangForKey(key));
    let i = this.programDay[key] ?? 0;
    if (i < 0) i = 0;
    if (i > tabs.length - 1) i = Math.max(0, tabs.length - 1);
    this.programDay[key] = i;
    return i;
  }

  selectProgramDay(key: string, index: number): void {
    this.programDay[key] = index;
  }

  /** The active day's tab object, or null when the section has no days. */
  activeTab(key: string): ProgramTab | null {
    const tabs = this.programTabs(key, this.sectionLangForKey(key));
    return tabs[this.activeProgramDay(key)] || null;
  }

  /** Resolve a section's role language from its block key. */
  private sectionLangForKey(key: string): string {
    const section = this.contentSections.find((s) => s.key === key);
    return section ? this.sectionLang(section) : this.roleLanguages.system || 'en';
  }

  addTab(key: string, lang: string): void {
    const tabs = this.programTabs(key, lang);
    tabs.push({ title: 'New day', heading: '', items: [] });
    this.programDay[key] = tabs.length - 1;
  }

  removeTab(key: string, lang: string, index: number): void {
    this.programTabs(key, lang).splice(index, 1);
    if ((this.programDay[key] ?? 0) >= index) {
      this.programDay[key] = Math.max(0, (this.programDay[key] ?? 0) - 1);
    }
  }

  // ---- agenda item helpers (within the active day) ----------------------

  private tabItems(tab: ProgramTab): AgendaItem[] {
    if (!tab.items) tab.items = [];
    return tab.items;
  }

  addAgendaItem(tab: ProgramTab): void {
    this.tabItems(tab).push({ time: '', text: '' });
  }

  removeAgendaItem(tab: ProgramTab, index: number): void {
    this.tabItems(tab).splice(index, 1);
  }

  moveAgendaItem(tab: ProgramTab, index: number, dir: -1 | 1): void {
    const items = this.tabItems(tab);
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target], items[index]];
  }

  /**
   * Parse a legacy program-tab HTML body (an <h2> heading + an <ol> of <li>
   * rows) into a structured heading + slots, so older stored content and the
   * built-in defaults load into the new editor. Each <li> becomes one slot
   * (time from <time>, content from <p>); rows without a <time> keep an empty
   * time. Links/formatting are preserved as inline HTML in the content.
   */
  private parseTabHtml(html: string): { heading: string; items: AgendaItem[] } {
    const result = { heading: '', items: [] as AgendaItem[] };
    if (!html) return result;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const h2 = doc.querySelector('h2');
      if (h2) result.heading = (h2.textContent || '').trim();
      const normalize = (s: string) => (s || '').replace(/\u00a0/g, ' ').trim();
      doc.querySelectorAll('li').forEach((li) => {
        const time = li.querySelector('time');
        const p = li.querySelector('p');
        result.items.push({
          time: time ? normalize(time.textContent || '') : '',
          text: p ? (p.innerHTML || '').trim() : (li.innerHTML || '').trim(),
        });
      });
    } catch {
      // Unparseable body: leave as an empty day (the editor still works).
    }
    return result;
  }

  /** Normalize a stored/default ProgramTab to the structured items shape. */
  private normalizeTab(tab: ProgramTab): ProgramTab {
    if (tab.items && tab.items.length) {
      return { title: tab.title || '', heading: tab.heading || '', items: tab.items };
    }
    const parsed = this.parseTabHtml(tab.html || '');
    return {
      title: tab.title || '',
      heading: tab.heading || parsed.heading,
      items: parsed.items,
    };
  }

  langLabel(code: string): string {
    const found = this.availableLanguages.find((l) => l.code === code);
    return found ? found.label : code;
  }

  // ---- Stage 5B: lists --------------------------------------------------

  private applyLists(s: { [key: string]: any }): void {
    // Activities: one array per active language, seeded from defaults when the
    // DB has nothing, then padded so every language has the same row count.
    const stored = s['activities'] || {};
    this.activitiesByLang = {};
    let maxLen = 0;
    for (const lang of this.languages) {
      const arr = Array.isArray(stored[lang])
        ? stored[lang].slice()
        : (ACTIVITY_DEFAULTS[lang] || ACTIVITY_DEFAULTS['en'] || []).slice();
      this.activitiesByLang[lang] = arr;
      maxLen = Math.max(maxLen, arr.length);
    }
    for (const lang of this.languages) {
      while (this.activitiesByLang[lang].length < maxLen) this.activitiesByLang[lang].push('');
    }

    // Exhibitor categories: single language-neutral list.
    const cats = s['exhibitor_categories'];
    this.categories = Array.isArray(cats) ? cats.slice() : CATEGORY_DEFAULTS.slice();
  }

  get activityRowCount(): number {
    return this.languages.reduce((m, lang) => Math.max(m, (this.activitiesByLang[lang] || []).length), 0);
  }

  get activityRows(): number[] {
    return Array.from({ length: this.activityRowCount }, (_, i) => i);
  }

  /** Ensure every active language has a slot at index i (keeps rows aligned). */
  private ensureActivityCell(lang: string, i: number): void {
    if (!this.activitiesByLang[lang]) this.activitiesByLang[lang] = [];
    while (this.activitiesByLang[lang].length <= i) this.activitiesByLang[lang].push('');
  }

  addActivityRow(): void {
    for (const lang of this.languages) this.ensureActivityCell(lang, this.activityRowCount);
  }

  removeActivityRow(i: number): void {
    for (const lang of this.languages) {
      if (this.activitiesByLang[lang]) this.activitiesByLang[lang].splice(i, 1);
    }
  }

  moveActivityRow(i: number, dir: -1 | 1): void {
    const target = i + dir;
    if (target < 0 || target >= this.activityRowCount) return;
    for (const lang of this.languages) {
      const arr = this.activitiesByLang[lang];
      if (arr) [arr[i], arr[target]] = [arr[target] ?? '', arr[i] ?? ''];
    }
  }

  addCategory(): void {
    this.categories.push('');
  }

  removeCategory(i: number): void {
    this.categories.splice(i, 1);
  }

  moveCategory(i: number, dir: -1 | 1): void {
    const target = i + dir;
    if (target < 0 || target >= this.categories.length) return;
    [this.categories[i], this.categories[target]] = [this.categories[target], this.categories[i]];
  }

  saveLists(): void {
    this.configService
      .saveConfig({
        activities: this.activitiesByLang,
        exhibitor_categories: this.categories,
      })
      .subscribe(() => this.flash('Lists saved.'));
  }

  // ---- Shared desk pairs -----------------------------------------------

  /** Read stored desk pairs from the settings group into editable rows. */
  private applyDeskPairs(s: { [key: string]: any }): void {
    const stored = s['exhibitor_desk_pairs'];
    const pairs = stored && Array.isArray(stored.pairs) ? stored.pairs : [];
    this.deskPairs = pairs
      .map((p: any) => ({ a: Number(p.a) || 0, b: Number(p.b) || 0 }))
      .filter((p: { a: number; b: number }) => p.a > 0 && p.b > 0);
  }

  /** Load the exhibitor list once, for the pair dropdowns (label = company/name). */
  private loadExhibitorOptions(): void {
    this.exhibitorService.getExhibitors(1, 1000, 'organisation', 'asc', '', '').subscribe({
      next: (res) => {
        this.exhibitorOptions = (res.data || [])
          .map((e) => ({
            id: e.t_exhibitorid,
            label: `${(e.organisation || '').trim()} — ${(e.vorname || '').trim()} ${(e.name || '').trim()}`.trim(),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      },
      error: () => {
        this.exhibitorOptions = [];
      },
    });
  }

  /** Friendly label for a stored exhibitor id (falls back to the raw id). */
  exhibitorLabel(id: number): string {
    const opt = this.exhibitorOptions.find((o) => o.id === id);
    return opt ? opt.label : id ? `#${id}` : '';
  }

  addDeskPair(): void {
    this.deskPairs.push({ a: 0, b: 0 });
    this.deskPairsMessage = '';
  }

  removeDeskPair(i: number): void {
    this.deskPairs.splice(i, 1);
    this.deskPairsMessage = '';
  }

  saveDeskPairs(): void {
    // Validate: both sides chosen, not the same exhibitor, and no exhibitor
    // appears in more than one pair (each desk links exactly two exhibitors).
    const seen = new Set<number>();
    for (const p of this.deskPairs) {
      if (!p.a || !p.b) {
        this.deskPairsMessage = 'Each pair needs two exhibitors selected.';
        return;
      }
      if (p.a === p.b) {
        this.deskPairsMessage = 'A pair must be two different exhibitors.';
        return;
      }
      if (seen.has(p.a) || seen.has(p.b)) {
        this.deskPairsMessage = 'Each exhibitor can only be in one shared desk pair.';
        return;
      }
      seen.add(p.a);
      seen.add(p.b);
    }

    this.deskPairsMessage = '';
    this.configService
      .saveConfig({ exhibitor_desk_pairs: { pairs: this.deskPairs } })
      .subscribe(() => this.flash('Shared desk pairs saved.'));
  }

  // ---- import / bootstrap ----------------------------------------------

  toggleImport(): void {
    this.importOpen = !this.importOpen;
  }

  onImportFile(event: any): void {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.importFileName = file.name || '';
    const reader = new FileReader();
    reader.onload = () => {
      this.importText = String(reader.result || '');
      this.applyImport();
    };
    reader.readAsText(file);
  }

  /**
   * Parse a pasted/uploaded legacy config file and pre-fill the form. Accepts
   * three formats, auto-detected from the file name + content:
   *   - JSON   API/webapp client config (event, account_info, event_cities, txt_, …)
   *   - .ts    Angular environment.*.ts (export const environment = { … })
   *   - .php   legacy *_conf.inc.php ($_VAR = "…";)
   * Only non-empty source values are applied; nothing is saved until the user
   * reviews and presses save.
   */
  applyImport(): void {
    const { format, data } = parseImport(this.importFileName, this.importText);
    if (!data) {
      this.importMessage =
        format === 'unknown'
          ? 'Unrecognized file — upload a client JSON, an environment.*.ts, or a *_conf.inc.php file.'
          : `Could not parse this ${format.toUpperCase()} file — please check its contents.`;
      return;
    }

    const mapped: string[] = [];
    const apply = (label: string, value: any, set: (v: any) => void) => {
      if (value !== undefined && value !== null && value !== '') {
        set(value);
        mapped.push(label);
      }
    };

    // ---- event ----
    apply('event.title', data.event.title, (v) => (this.event.title = v));
    apply('event.year', data.event.year, (v) => (this.event.year = v));
    apply('event.www_url', data.event.www_url, (v) => (this.event.www_url = v));
    apply('event.email', data.event.email, (v) => (this.event.email = v));
    apply('event.frontend_url', data.event.frontend_url, (v) => (this.event.frontend_url = v));
    apply('event.welcome_heading', data.event.welcome_heading, (v) => (this.event.welcome_heading = v));
    apply('event.welcome', data.event.welcome, (v) => (this.event.welcome = v));
    // (welcome heading/body are now persisted in event_settings, not the client JSON)
    apply('event.mail_subject', data.event.mail_subject, (v) => (this.event.mail_subject = v));
    apply('event.complete_profile_by', data.event.complete_profile_by, (v) => (this.event.complete_profile_by = v));
    apply(
      'event.validate_registration_latest',
      data.event.validate_registration_latest,
      (v) => (this.event.validate_registration_latest = v)
    );

    // ---- logistics ----
    apply('logistics.hotel', data.logistics.hotel, (v) => (this.logistics.hotel = v));
    apply('logistics.event_space', data.logistics.event_space, (v) => (this.logistics.event_space = v));
    apply('logistics.event_date', data.logistics.event_date, (v) => (this.logistics.event_date = v));
    apply('logistics.book_night', data.logistics.book_night, (v) => (this.logistics.book_night = v));
    apply('logistics.cancel_date', data.logistics.cancel_date, (v) => (this.logistics.cancel_date = v));
    apply('logistics.day1', data.logistics.day1, (v) => (this.logistics.day1 = v));
    apply('logistics.day2', data.logistics.day2, (v) => (this.logistics.day2 = v));

    // ---- media ----
    apply('media.hero_video_mp4', data.media.hero_video_mp4, (v) => (this.media.hero_video_mp4 = v));
    apply('media.hero_video_webm', data.media.hero_video_webm, (v) => (this.media.hero_video_webm = v));
    apply('media.pdf_logo', data.media.pdf_logo, (v) => (this.media.pdf_logo = v));
    apply('media.pdf_footer', data.media.pdf_footer, (v) => (this.media.pdf_footer = v));

    // ---- flags ----
    if (Object.keys(data.flags).length) {
      this.flags = { ...this.flags, ...data.flags };
      mapped.push(`flags (${Object.keys(data.flags).length})`);
    }

    // ---- notifications ----
    if (Object.keys(data.notifications).length) {
      this.notifications = { ...this.notifications, ...data.notifications };
      mapped.push(`notifications (${Object.keys(data.notifications).length})`);
    }

    // ---- test-mail recipients ----
    const tm: any = {};
    if (data.testmail.visitor) tm.visitor = data.testmail.visitor;
    if (data.testmail.exhibitor) tm.exhibitor = data.testmail.exhibitor;
    if (data.testmail.atout) tm.atout = data.testmail.atout;
    if (Object.keys(tm).length) {
      this.testmail = { ...this.testmail, ...tm };
      mapped.push(`testmail (${Object.keys(tm).length})`);
    }

    // languages are derived from the per-role main languages, so an env file's
    // language list is not imported here.

    // ---- agenda days ----
    if (data.days && data.days.length) {
      this.days = data.days.map((d) => ({ ...d }));
      mapped.push(`agenda.days (${this.days.length})`);
    }

    // ---- activities (re-padded so every active language has equal rows) ----
    if (data.activities && Object.keys(data.activities).length) {
      let maxLen = 0;
      for (const lang of Object.keys(data.activities)) {
        const arr = (data.activities[lang] || []).slice();
        this.activitiesByLang[lang] = arr;
        maxLen = Math.max(maxLen, arr.length);
      }
      for (const lang of this.languages) {
        if (!this.activitiesByLang[lang]) this.activitiesByLang[lang] = [];
        while (this.activitiesByLang[lang].length < maxLen) this.activitiesByLang[lang].push('');
      }
      mapped.push('activities');
    }

    // ---- exhibitor categories ----
    if (data.categories && data.categories.length) {
      this.categories = data.categories.slice();
      mapped.push(`exhibitor_categories (${this.categories.length})`);
    }

    // ---- dynamic profile fields (staged; written by Save everything) ----
    if (data.dynamicFields) {
      this.pendingDynamicFields = data.dynamicFields;
      mapped.push('dynamic-fields');
      // reflect into the config-file editor if it's showing that file
      if (this.selectedFileId === 'event/dynamic-fields') {
        this.fileContent = JSON.stringify(this.pendingDynamicFields, null, 2);
      }
    }

    // ---- booking restrictions (na_*/nb_*; staged; written by Save everything) ----
    if (data.bookingRestrictions) {
      this.pendingBookingRestrictions = data.bookingRestrictions;
      mapped.push('booking-restrictions');
      if (this.selectedFileId === 'event/booking-restrictions') {
        this.fileContent = JSON.stringify(this.pendingBookingRestrictions, null, 2);
      }
    }

    this.importMessage = mapped.length
      ? `Imported ${mapped.length} field(s) from ${format.toUpperCase()}: ${mapped.join(', ')}. Review each tab, then Save (or “Save everything”).`
      : 'No recognizable fields found in this file.';
  }

  /** Persist every section at once — convenient right after an import. */
  saveAll(): void {
    this.configService
      .saveConfig({
        event: this.event,
        logistics: this.logistics,
        media: this.media,
        flags: this.flags,
        notifications: this.notifications,
        testmail: this.testmail,
        activities: this.activitiesByLang,
        exhibitor_categories: this.categories,
        system: { languages: this.languages, role_languages: this.roleLanguages },
      })
      .subscribe(() => {
        this.configService.saveDays(this.days).subscribe(() => {
          this.saveImportedConfigFiles((extra) =>
            this.flash(extra.length ? `All settings saved (incl. ${extra.join(', ')}).` : 'All settings saved.')
          );
        });
      });
  }

  /**
   * Persist any config files staged by an import (dynamic-fields,
   * booking-restrictions), one after another, then invoke done() with the list
   * of file labels that were written.
   */
  private saveImportedConfigFiles(done: (saved: string[]) => void): void {
    const tasks: { id: string; label: string; content: any }[] = [];
    if (this.pendingDynamicFields) {
      tasks.push({ id: 'event/dynamic-fields', label: 'dynamic fields', content: this.pendingDynamicFields });
    }
    if (this.pendingBookingRestrictions) {
      tasks.push({ id: 'event/booking-restrictions', label: 'booking restrictions', content: this.pendingBookingRestrictions });
    }
    const saved: string[] = [];
    let i = 0;
    const next = () => {
      if (i >= tasks.length) {
        this.pendingDynamicFields = null;
        this.pendingBookingRestrictions = null;
        done(saved);
        return;
      }
      const t = tasks[i++];
      this.configService.saveConfigFile(t.id, JSON.stringify(t.content, null, 2)).subscribe(() => {
        saved.push(t.label);
        next();
      });
    };
    next();
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ---- agenda day helpers ----------------------------------------------

  addDay(): void {
    const nextNo = this.days.reduce((max, d) => Math.max(max, d.day_no), 0) + 1;
    this.days.push({
      day_no: nextNo,
      enabled: 1,
      caption: '',
      event_date: null,
      slot_start: '09:00',
      slot_end: '17:00',
      slot_interval: 20,
      breaks: '',
      break_caption: '',
      spots: 0,
    });
  }

  removeDay(index: number): void {
    this.days.splice(index, 1);
  }

  // ---- languages by role (read-only facts for the System tab) -----------
  // None of these are stored, switchable per-user settings: each is fixed per
  // surface (admin), or per client/build (visitor/exhibitor). Surfaced so the
  // operator can see them, distinct from the editable content languages above.

  readonly adminUiLanguage = 'English (en-EN)';

  private get effectiveClient(): string {
    return this.client || (environment as any).client || '';
  }

  get visitorUiLanguage(): string {
    return this.effectiveClient === 'french-rendez-vous' ? 'English (en)' : 'German (de)';
  }

  get exhibitorUiLanguage(): string {
    // exhibitor UI is compiled into the widget build (environment.prod-<client>.ts)
    return this.visitorUiLanguage;
  }

  get eventCountryCode(): string {
    return (environment as any).veranstaltungs_land || '';
  }

  // ---- media ------------------------------------------------------------

  mediaUrl(relativePath: string): string {
    if (!relativePath) return '';
    return `${environment.eplusSystem}/_fileDB/${relativePath}`;
  }

  onMediaUploaded(key: keyof MediaForm, event: any): void {
    const serverId = event?.file?.serverId;
    if (serverId) {
      this.media[key] = serverId;
      this.saveMedia(
        key === 'brand_logo'
          ? 'Brand logo saved (reload the public app to take effect).'
          : 'Media saved.'
      );
    }
  }

  // ---- FilePond options -------------------------------------------------

  private buildPondOptions(assetkind: 'image' | 'video', acceptedFileTypes: string[], typeLabel: string) {
    const self = this;
    return {
      class: 'my-filepond',
      credits: false,
      multiple: false,
      allowFileTypeValidation: true,
      acceptedFileTypes,
      labelFileTypeNotAllowed: 'File of invalid type: only ' + typeLabel,
      labelIdle: 'Drop file here or <a style="text-decoration:underline;">browse</a> ...',
      // videos can be large
      allowFileSizeValidation: assetkind === 'video',
      maxFileSize: assetkind === 'video' ? '2000MB' : undefined,
      server: {
        process: (
          fieldName: string,
          file: File,
          metadata: any,
          load: (id: string) => void,
          error: (msg: string) => void,
          progress: (computable: boolean, loaded: number, total: number) => void,
          abort: () => void
        ) => {
          const formData = new FormData();
          formData.append(fieldName, file, file.name);
          formData.append('filepond', file.name);
          formData.append('uid', '' + (self.authQuery.userid ?? ''));
          formData.append('assetkind', assetkind);
          // no `field`: the component persists the media object explicitly

          const request = new XMLHttpRequest();
          request.open('POST', environment.serverUrl + '/config/asset');
          request.setRequestHeader('Access-Control-Allow-Origin', '*');
          request.setRequestHeader('X-User-Id', '' + (self.authQuery.userid ?? ''));
          request.setRequestHeader('Authorization', `Bearer ${self.authQuery.access_token}`);

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              load(request.responseText);
            } else {
              error('upload failed');
            }
          };

          request.send(formData);

          return {
            abort: () => {
              request.abort();
              abort();
            },
          };
        },
      },
    };
  }

  private buildInforeisePondOptions() {
    const self = this;
    return {
      class: 'my-filepond',
      credits: false,
      multiple: false,
      allowFileTypeValidation: true,
      acceptedFileTypes: ['application/pdf'],
      labelFileTypeNotAllowed: 'File of invalid type: only PDF',
      labelIdle: 'Drop PDF here or <a style="text-decoration:underline;">browse</a> …',
      server: {
        process: (
          fieldName: string,
          file: File,
          metadata: any,
          load: (id: string) => void,
          error: (msg: string) => void,
          progress: (computable: boolean, loaded: number, total: number) => void,
          abort: () => void
        ) => {
          const formData = new FormData();
          formData.append(fieldName, file, file.name);
          formData.append('filepond', file.name);
          formData.append('uid', '' + (self.authQuery.userid ?? ''));
          formData.append('assetkind', 'inforeisen');

          const request = new XMLHttpRequest();
          request.open('POST', environment.serverUrl + '/config/asset');
          request.setRequestHeader('Access-Control-Allow-Origin', '*');
          request.setRequestHeader('X-User-Id', '' + (self.authQuery.userid ?? ''));
          request.setRequestHeader('Authorization', `Bearer ${self.authQuery.access_token}`);

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              load(request.responseText);
            } else {
              error('upload failed');
            }
          };

          request.send(formData);

          return {
            abort: () => {
              request.abort();
              abort();
            },
          };
        },
      },
    };
  }

  /**
   * FilePond options for the gallery: multi-file image upload that posts each
   * file to /config/gallery with the currently selected category. Reads
   * `this.galleryCategory` at send time so changing the category applies to
   * subsequent uploads without rebuilding the options.
   */
  private buildGalleryPondOptions() {
    const self = this;
    return {
      class: 'my-filepond',
      credits: false,
      allowMultiple: true,
      maxParallelUploads: 3,
      allowFileTypeValidation: true,
      acceptedFileTypes: ['image/png', 'image/jpeg', 'image/webp'],
      labelFileTypeNotAllowed: 'File of invalid type: only JPG, PNG, WebP',
      labelIdle: 'Drop images here or <a style="text-decoration:underline;">browse</a> — batch upload supported',
      server: {
        process: (
          fieldName: string,
          file: File,
          metadata: any,
          load: (id: string) => void,
          error: (msg: string) => void,
          progress: (computable: boolean, loaded: number, total: number) => void,
          abort: () => void
        ) => {
          const formData = new FormData();
          formData.append(fieldName, file, file.name);
          formData.append('filepond', file.name);
          formData.append('uid', '' + (self.authQuery.userid ?? ''));
          formData.append('category', (self.galleryCategory || 'general').trim() || 'general');

          const request = new XMLHttpRequest();
          request.open('POST', environment.serverUrl + '/config/gallery');
          request.setRequestHeader('Access-Control-Allow-Origin', '*');
          request.setRequestHeader('X-User-Id', '' + (self.authQuery.userid ?? ''));
          request.setRequestHeader('Authorization', `Bearer ${self.authQuery.access_token}`);

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              load(request.responseText);
            } else {
              error('upload failed');
            }
          };

          request.send(formData);

          return {
            abort: () => {
              request.abort();
              abort();
            },
          };
        },
      },
    };
  }

  /**
   * FilePond options for the single main hero image. Uploads into the reserved
   * "_hero" gallery folder (excluded from album listings) and the server path is
   * stored into galleryMeta.hero on success.
   */
  private buildGalleryHeroPondOptions() {
    const self = this;
    return {
      class: 'my-filepond',
      credits: false,
      multiple: false,
      allowFileTypeValidation: true,
      acceptedFileTypes: ['image/png', 'image/jpeg', 'image/webp'],
      labelFileTypeNotAllowed: 'File of invalid type: only JPG, PNG, WebP',
      labelIdle: 'Drop hero image here or <a style="text-decoration:underline;">browse</a> ...',
      server: {
        process: (
          fieldName: string,
          file: File,
          metadata: any,
          load: (id: string) => void,
          error: (msg: string) => void,
          progress: (computable: boolean, loaded: number, total: number) => void,
          abort: () => void
        ) => {
          const formData = new FormData();
          formData.append(fieldName, file, file.name);
          formData.append('filepond', file.name);
          formData.append('uid', '' + (self.authQuery.userid ?? ''));
          formData.append('category', '_hero');

          const request = new XMLHttpRequest();
          request.open('POST', environment.serverUrl + '/config/gallery');
          request.setRequestHeader('Access-Control-Allow-Origin', '*');
          request.setRequestHeader('X-User-Id', '' + (self.authQuery.userid ?? ''));
          request.setRequestHeader('Authorization', `Bearer ${self.authQuery.access_token}`);

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              load(request.responseText);
            } else {
              error('upload failed');
            }
          };

          request.send(formData);

          return {
            abort: () => {
              request.abort();
              abort();
            },
          };
        },
      },
    };
  }

  // ---- Stage 5D: raw config-file editor --------------------------------

  loadConfigFiles(): void {
    this.configService.listConfigFiles().subscribe({
      next: (res) => {
        this.configFiles = res.files || [];
        // auto-select the first writable file, else the first file
        if (!this.selectedFileId && this.configFiles.length) {
          const writable = this.configFiles.find((f) => !f.readonly);
          this.selectConfigFile((writable || this.configFiles[0]).id);
        }
      },
      error: () => {
        this.configFiles = [];
      },
    });
  }

  decFileFont(): void {
    this.fileFontSize = Math.max(8, this.fileFontSize - 1);
  }

  incFileFont(): void {
    this.fileFontSize = Math.min(22, this.fileFontSize + 1);
  }

  selectConfigFile(id: string): void {
    this.selectedFileId = id;
    this.fileError = '';
    this.fileMessage = '';
    this.fileContent = '';
    this.configService.getConfigFile(id).subscribe({
      next: (res) => {
        this.fileContent = res.content || '';
        this.fileReadonly = !!res.readonly;
        this.fileType = res.type || '';
      },
      error: () => {
        this.fileError = 'Could not load this file.';
      },
    });
  }

  /** Validate the editor content as JSON; populates fileError. */
  validateFileJson(): boolean {
    if (this.fileType !== 'json') {
      return true;
    }
    try {
      JSON.parse(this.fileContent);
      this.fileError = '';
      return true;
    } catch (e: any) {
      this.fileError = 'Invalid JSON: ' + (e?.message || 'parse error');
      return false;
    }
  }

  saveConfigFile(): void {
    if (this.fileReadonly || !this.selectedFileId) {
      return;
    }
    if (!this.validateFileJson()) {
      return;
    }
    this.configService.saveConfigFile(this.selectedFileId, this.fileContent).subscribe({
      next: (res) => {
        if (res && res.saved) {
          this.fileMessage = 'Saved (a backup was created).';
          setTimeout(() => (this.fileMessage = ''), 3000);
          this.loadConfigFiles();
        } else {
          this.fileError = (res && res.error) || 'Save failed.';
        }
      },
      error: (err) => {
        this.fileError = err?.error?.error || 'Save failed (the file may be read-only).';
      },
    });
  }

  // ---- image picker -----------------------------------------------------

  /**
   * Open the picker for the editor that triggered the toolbar button. The
   * TinyMCE button onAction runs outside Angular's zone (the tinymce-angular
   * wrapper inits the editor with ngZone.runOutsideAngular), so we re-enter the
   * zone to make change detection render the overlay.
   */
  openImagePicker(editor: any): void {
    this.ngZone.run(() => {
      this.activeEditor = editor;
      this.pickerOpen = true;
      this.pickerLoading = true;
      this.configService.listContentImages().subscribe({
        next: (res) => {
          this.pickerImages = res.images || [];
          this.pickerLoading = false;
        },
        error: () => {
          this.pickerImages = [];
          this.pickerLoading = false;
        },
      });
    });
  }

  closeImagePicker(): void {
    this.ngZone.run(() => {
      this.pickerOpen = false;
      this.activeEditor = null;
    });
  }

  /** Reload the picker grid (after an in-dialog upload finishes). */
  refreshPickerImages(): void {
    this.ngZone.run(() => {
      this.pickerLoading = true;
      this.configService.listContentImages().subscribe({
        next: (res) => {
          this.pickerImages = res.images || [];
          this.pickerLoading = false;
        },
        error: () => {
          this.pickerLoading = false;
        },
      });
    });
  }

  /** A file finished uploading inside the picker — show it in the grid. */
  onPickerUploaded(): void {
    this.refreshPickerImages();
  }

  /**
   * FilePond options for the in-dialog uploader: posts to /config/asset with
   * assetkind 'element' (same store as TinyMCE inline images) so uploads land in
   * the same library the picker browses. The grid refreshes on completion.
   */
  private buildPickerPondOptions() {
    const self = this;
    return {
      class: 'my-filepond',
      credits: false,
      allowMultiple: true,
      maxParallelUploads: 3,
      allowFileTypeValidation: true,
      acceptedFileTypes: ['image/png', 'image/jpeg', 'image/webp', 'image/gif'],
      labelFileTypeNotAllowed: 'File of invalid type: only JPG, PNG, WebP, GIF',
      labelIdle: 'Drop images here or <a style="text-decoration:underline;">browse</a> to upload …',
      server: {
        process: (
          fieldName: string,
          file: File,
          metadata: any,
          load: (id: string) => void,
          error: (msg: string) => void,
          progress: (computable: boolean, loaded: number, total: number) => void,
          abort: () => void
        ) => {
          const formData = new FormData();
          formData.append(fieldName, file, file.name);
          formData.append('filepond', file.name);
          formData.append('uid', '' + (self.authQuery.userid ?? ''));
          formData.append('assetkind', 'element');

          const request = new XMLHttpRequest();
          request.open('POST', environment.serverUrl + '/config/asset');
          request.setRequestHeader('Access-Control-Allow-Origin', '*');
          request.setRequestHeader('X-User-Id', '' + (self.authQuery.userid ?? ''));
          request.setRequestHeader('Authorization', `Bearer ${self.authQuery.access_token}`);

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              load(request.responseText);
            } else {
              error('upload failed');
            }
          };

          request.send(formData);

          return {
            abort: () => {
              request.abort();
              abort();
            },
          };
        },
      },
    };
  }

  /** Build the full served URL for a listed image. */
  imageUrl(img: ContentImage): string {
    return `${this.configService.fileDbBaseUrl}/${img.path}`;
  }

  /** Insert the chosen image into the active editor and close the picker. */
  pickImage(img: ContentImage): void {
    this.ngZone.run(() => {
      if (this.activeEditor) {
        const url = this.imageUrl(img);
        this.activeEditor.insertContent(`<img src="${url}" style="max-width:100%;" />`);
      }
      this.closeImagePicker();
    });
  }

  // ---- Admin user management -------------------------------------------

  loadAdminUsers(): void {
    this.configService.listAdminUsers().subscribe({
      next: (res) => {
        this.adminUsers = res.users || [];
      },
      error: () => {
        this.adminUsers = [];
      },
    });
  }

  /** Start creating a fresh admin (clears the form). */
  newAdminUser(): void {
    this.adminEditing = true;
    this.adminError = '';
    this.adminForm = { name: '', login: '', email: '', company: '', password: '', active: 1 };
  }

  /** Load an existing admin into the form for editing. */
  editAdminUser(u: AdminUser): void {
    if (u.su === 1) return; // superusers are read-only here
    this.adminEditing = true;
    this.adminError = '';
    this.adminForm = {
      id: u.id,
      name: u.name,
      login: u.login,
      email: u.email,
      company: u.company,
      password: '',
      active: u.active,
    };
  }

  cancelAdminEdit(): void {
    this.adminEditing = false;
    this.adminError = '';
  }

  /** True while editing an already-persisted admin (enables avatar upload). */
  get adminFormIsExisting(): boolean {
    return !!this.adminForm.id;
  }

  saveAdminUser(): void {
    this.adminError = '';
    const f = this.adminForm;
    if (!f.name.trim() || !f.login.trim()) {
      this.adminError = 'Name and login are required.';
      return;
    }
    if (!f.id && f.password.trim().length < 5) {
      this.adminError = 'A password of at least 5 characters is required for a new admin.';
      return;
    }
    const payload = {
      id: f.id,
      name: f.name.trim(),
      login: f.login.trim(),
      email: f.email.trim(),
      company: f.company.trim(),
      password: f.password,
      active: f.active,
    };
    this.configService.saveAdminUser(payload).subscribe({
      next: (res) => {
        if (res && res.saved) {
          // keep the form open on the just-created/edited admin so the avatar
          // uploader (which needs an id) becomes available immediately.
          if (res.id) this.adminForm.id = res.id;
          this.adminForm.password = '';
          this.loadAdminUsers();
          this.flash('Admin saved.');
        } else {
          this.adminError = (res && res.msg) || (res && res.error) || 'Could not save admin.';
        }
      },
      error: (err) => {
        this.adminError = err?.error?.msg || err?.error?.error || 'Could not save admin (superuser required).';
      },
    });
  }

  deactivateAdminUser(u: AdminUser): void {
    if (u.su === 1) return;
    if (!confirm(`Deactivate admin "${u.name}" (${u.login})? They will no longer be able to log in.`)) {
      return;
    }
    this.configService.deactivateAdminUser(u.id).subscribe({
      next: (res) => {
        if (res && res.deactivated) {
          this.loadAdminUsers();
          this.flash('Admin deactivated.');
        } else {
          this.adminError = (res && res.msg) || (res && res.error) || 'Could not deactivate admin.';
        }
      },
      error: (err) => {
        this.adminError = err?.error?.msg || err?.error?.error || 'Could not deactivate admin.';
      },
    });
  }

  /** Re-enable a previously deactivated admin (active + isapproved -> 1). */
  reactivateAdminUser(u: AdminUser): void {
    if (u.su === 1) return;
    this.configService.saveAdminUser({ id: u.id, active: 1 }).subscribe({
      next: () => {
        this.loadAdminUsers();
        this.flash('Admin reactivated.');
      },
      error: () => (this.adminError = 'Could not reactivate admin.'),
    });
  }

  /** Display URL for an admin's profile image (empty when none set). */
  adminAvatarUrl(u: AdminUser): string {
    if (!u.profile_image) return '';
    return `${this.configService.adminAvatarBaseUrl}/${u.profile_image}`;
  }

  /** A file finished uploading for the edited admin — refresh the list. */
  onAdminAvatarUploaded(): void {
    this.loadAdminUsers();
    this.flash('Profile image updated.');
  }

  /**
   * FilePond options for the admin avatar uploader. Posts the file plus the
   * acting uid and the target admin id (read at send time from adminForm) to
   * /config/admin-avatar, which stores it under images/admin and writes
   * t_beuser.profile_image.
   */
  private buildAdminAvatarPondOptions() {
    const self = this;
    return {
      class: 'my-filepond',
      credits: false,
      multiple: false,
      allowFileTypeValidation: true,
      acceptedFileTypes: ['image/png', 'image/jpeg', 'image/webp'],
      labelFileTypeNotAllowed: 'File of invalid type: only JPG, PNG, WebP',
      labelIdle: 'Drop profile image here or <a style="text-decoration:underline;">browse</a> ...',
      server: {
        process: (
          fieldName: string,
          file: File,
          metadata: any,
          load: (id: string) => void,
          error: (msg: string) => void,
          progress: (computable: boolean, loaded: number, total: number) => void,
          abort: () => void
        ) => {
          const formData = new FormData();
          formData.append(fieldName, file, file.name);
          formData.append('filepond', file.name);
          formData.append('uid', '' + (self.authQuery.userid ?? ''));
          formData.append('id', '' + (self.adminForm.id ?? ''));

          const request = new XMLHttpRequest();
          request.open('POST', environment.serverUrl + '/config/admin-avatar');
          request.setRequestHeader('Access-Control-Allow-Origin', '*');
          request.setRequestHeader('X-User-Id', '' + (self.authQuery.userid ?? ''));
          request.setRequestHeader('Authorization', `Bearer ${self.authQuery.access_token}`);

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              load(request.responseText);
              self.ngZone.run(() => self.onAdminAvatarUploaded());
            } else {
              error('upload failed');
            }
          };

          request.send(formData);

          return {
            abort: () => {
              request.abort();
              abort();
            },
          };
        },
      },
    };
  }

  // ---- ui ---------------------------------------------------------------

  private flash(msg: string): void {
    this.message = msg;
    setTimeout(() => (this.message = ''), 3000);
  }
}
